import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel, flexRender, createColumnHelper,
} from '@tanstack/react-table';
import { motion, AnimatePresence, useMotionValue, useSpring, useMotionValueEvent } from 'motion/react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { format as formatDateFns } from 'date-fns';
import { Menu } from '@base-ui-components/react/menu';
import {
  Upload, Download, Search, Check, AlertCircle, Copy,
  ChevronDown, ChevronLeft, ChevronRight, Calendar as CalendarIcon, Ticket as TicketIcon, ArrowUpDown, ArrowUp, ArrowDown, ArrowLeft, StickyNote, X,
  FileText, Pencil,
} from 'lucide-react';
import { API_BASE } from '../config';
import { formatDate } from '../utils/formatDate';
import { hasEditAccess } from '../permissionConfig';
import { notifyEditBlocked } from '../components/EditAccessToast';

/* ============================================================================
 * Goods Received Note — redesigned SaaS-style interface, live at
 * /po-so-lifecycle/grn-management.
 *
 * SaaS visual pass — real components adapted from fetched source, not
 * paraphrased docs:
 *   - Counter        <- animata/text/counter.tsx (spring count-up via `motion`)
 *   - Status filter  <- animata/tabs/fluid-tabs.tsx (shared layoutId sliding pill)
 *   - Reconciliation <- animata/graphs/donut-chart.tsx (SVG stroke-dashoffset ring)
 *     ring
 *   - Date range     <- react-day-picker (engine under shadcn/ui's Calendar),
 *                       captionLayout="dropdown" per the user's reference demo
 *   - Month/Year     <- @base-ui-components/react Menu (what shadcn/ui's
 *     dropdown          DropdownMenu wraps), radio-item single-select
 *
 * Layout: persistent 2/3 (filters+table) / 1/3 (KPI <-> PO detail) split —
 * the right column never opens as an overlay; its content swaps in place.
 * ========================================================================== */

const MONTH_OPTIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  .map((m, i) => ({ label: m, value: String(i + 1).padStart(2, '0') }));
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR - 2, CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map(y => ({ label: String(y), value: String(y) }));

const ICON = { xs: 12, sm: 14, md: 16, lg: 20 };
const SHADOW_SM = 'var(--grn-shadow-sm)';
const SHADOW_MD = 'var(--grn-shadow-md)';
const SHADOW_LG = 'var(--grn-shadow-lg)';
const STICKY_TOP = 86; // approx header-bar height + gap; both the table header and right column stick below it
const GROW_SEARCH_BG = 'var(--grn-surface-subtle)'; // shared by the pill and its input so they're never visibly two different backgrounds
const GRN_SEARCH_COLLAPSED_WIDTH = 40;
const GRN_SEARCH_EXPANDED_WIDTH = 240;
// The exact delta the search grows by — also how far the Upload/Download
// button group slides left to stay clear of it, so the two stay in sync.
const GRN_SEARCH_EXPAND_DELTA = GRN_SEARCH_EXPANDED_WIDTH - GRN_SEARCH_COLLAPSED_WIDTH;

/* ---------------------------------------------------------------------- */
/* Data/business logic — identical to GRNManagement.jsx                    */
/* ---------------------------------------------------------------------- */

function displayStatus(status) {
  return status === 'Open' ? 'Discrepancies' : status;
}

const MONTHS = { Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6, Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12 };

// Zepto's current export date cells are plain ISO strings (e.g.
// "2026-09-14T00:00:00Z"), unlike every other vendor's ambiguous DD/MM or
// serial-number dates below — no manual day/month disambiguation needed,
// just lift the date portion.
function parseIsoDate(str) {
  const m = String(str || '').match(/^(\d{4}-\d{2}-\d{2})/);
  return m ? m[1] : null;
}
function parseAmazonDateTime(str) {
  const m = String(str || '').match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})/);
  if (!m) return null;
  const month = MONTHS[m[2]];
  if (!month) return null;
  return `${m[3]}-${String(month).padStart(2, '0')}-${String(m[1]).padStart(2, '0')}`;
}
function parseDMYDate(str) {
  const m = String(str || '').match(/^(\d{1,2})-(\d{1,2})-(\d{4})/);
  if (!m) return null;
  return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
}
function parseExcelSerialDate(serial) {
  if (typeof serial !== 'number' || isNaN(serial)) return null;
  const d = window.XLSX.SSF.parse_date_code(serial);
  if (!d || !d.y) return null;
  return `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;
}

const simplePOMapper = {
  requiredColumns: ['po', 'received quantity', 'status'],
  filter: row => String(row['status']).trim().toLowerCase() !== 'unconfirmed',
  map: row => ({ po_no: row['po'], grn_no: null, grn_qty: row['received quantity'], grn_date: null }),
};

const VENDOR_PARSERS = {
  'Zepto Limited': {
    requiredColumns: ['po code', 'grn code', 'grn quantity', 'grn date'],
    map: row => ({ po_no: row['po code'], grn_no: row['grn code'], grn_qty: row['grn quantity'], grn_date: parseIsoDate(row['grn date']) }),
    // Zepto's export is itemized per SKU line — several rows share the same
    // GRN Code, each carrying only that one SKU's quantity. grn_management
    // stores one row per GRN with a single total quantity, so these SKU-level
    // quantities must be summed by GRN Code before upload here; uploading
    // them as separate rows would just overwrite each other via
    // upsertGrnRow's match-by-grn_no (last SKU processed wins, the rest lost).
    aggregateBy: row => row.grn_no || row.po_no,
  },
  'Amazon Seller Centre': {
    requiredColumns: ['shipment id', 'units located', 'last updated', 'status'],
    filter: row => ['in transit', 'receiving'].includes(String(row['status']).trim().toLowerCase()),
    map: row => ({ po_no: row['shipment id'], grn_no: null, grn_qty: row['units located'], grn_date: parseAmazonDateTime(row['last updated']) }),
  },
  'RK World': simplePOMapper,
  'E Trade': simplePOMapper,
  'Nykaa E Retail Pvt Ltd': {
    requiredColumns: ['po number', 'grn number', 'grn qty', 'grn date'],
    map: row => ({ po_no: row['po number'], grn_no: row['grn number'], grn_qty: row['grn qty'], grn_date: parseDMYDate(row['grn date']) }),
  },
  'Shoppers Stop': {
    requiredColumns: ['rc no', 'rc date', 'po no', 'rc qty'],
    map: row => ({ po_no: row['po no'], grn_no: row['rc no'], grn_qty: row['rc qty'], grn_date: parseExcelSerialDate(row['rc date']) }),
  },
};
const VENDOR_NAMES = Object.keys(VENDOR_PARSERS);
const VENDOR_STYLE = [
  { tint: 'var(--grn-amber-tint)', color: 'var(--grn-amber)' }, { tint: 'var(--grn-purple-tint)', color: 'var(--grn-purple)' }, { tint: '#E4EDF5', color: '#436A93' },
  { tint: 'var(--grn-green-tint)', color: 'var(--grn-green)' }, { tint: '#F7E5EA', color: '#B05770' }, { tint: 'var(--grn-blue-tint)', color: 'var(--grn-blue)' },
];

function cellToHeaderKey(cell) { return String(cell).replace(/^﻿/, '').trim().toLowerCase(); }

function statusStyle(status) {
  if (status === 'Open') return { bg: 'var(--grn-amber-tint)', color: 'var(--grn-amber)', border: 'var(--grn-amber-border)' };
  return { bg: 'var(--grn-green-tint)', color: 'var(--grn-green)', border: 'var(--grn-green-border)' };
}

// A real Excel date cell, not formatted text — lets the sheet's own sort/
// filter/date-math work. Built from UTC calendar fields (matching formatDate
// above) so the day shown in Excel can't roll back a day the way a local-
// midnight Date would in timezones ahead of UTC.
function toExcelDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  // Date.UTC (not the local constructor) so the resulting Date's epoch lands
  // exactly on UTC midnight of the target day — SheetJS's date->serial
  // conversion (datenum) reads the JS Date's raw epoch, so a local-midnight
  // Date is off by the runtime's UTC offset and produces a fractional serial.
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
}
function toDateInputValue(value) {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

function groupKeyForRow(r) { return r.po_no ? String(r.po_no).trim().toUpperCase() : `__no_po_${r.id}`; }
function groupRowsByPo(rows) {
  const map = new Map();
  rows.forEach(r => {
    const key = groupKeyForRow(r);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(r);
  });
  return map;
}
function aggregateGroup(grns) {
  const totalGrnQty = grns.reduce((s, r) => s + (Number(r.grn_qty) || 0), 0);
  const invRow = grns.find(r => r.invoice_qty !== null && r.invoice_qty !== undefined && r.invoice_qty !== '');
  const invoiceQty = invRow ? Number(invRow.invoice_qty) : null;
  const diff = invoiceQty === null ? null : totalGrnQty - invoiceQty;
  const partyNames = [...new Set(grns.map(r => r.party_name).filter(Boolean))];
  return { grns, totalGrnQty, invoiceQty, diff, partyNames, poNo: grns[0]?.po_no || '', poDate: grns[0]?.po_date || null };
}
function worstStatusOf(group) {
  const statuses = group.grns.map(r => r.status);
  if (statuses.includes('Open')) return 'Open';
  if (statuses.includes('Resolved')) return 'Resolved';
  return 'Matched';
}
function isMixedStatus(group) { return new Set(group.grns.map(r => r.status)).size > 1; }
function hasAnyTicket(group) { return group.grns.some(r => parseTickets(r.ticket_no).length > 0); }
// A PO can have several GRNs, each with its own ticket(s) — collect every
// unique ticket number across all of them for the PO-level detail view.
function allTicketsForGroup(group) {
  const all = group.grns.flatMap(r => parseTickets(r.ticket_no));
  return [...new Set(all)];
}

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function parseTickets(ticketNo) { return (ticketNo || '').split(',').map(t => t.trim()).filter(Boolean); }
const NO_TICKET_LABEL = 'No Ticket';

function copyToClipboard(text, onDone) {
  if (!text) return;
  const value = String(text);
  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard.writeText(value).then(() => onDone && onDone()).catch(() => {});
  } else {
    const ta = document.createElement('textarea');
    ta.value = value;
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); onDone && onDone(); } catch { /* ignore */ }
    document.body.removeChild(ta);
  }
}

/* ---------------------------------------------------------------------- */
/* Counter — adapted from animata/text/counter.tsx: spring count-up on      */
/* mount/value change instead of a static number.                          */
/* ---------------------------------------------------------------------- */
function Counter({ value, style }) {
  const ref = useRef(null);
  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, { damping: 60, stiffness: 80 });

  useEffect(() => { motionValue.set(value || 0); }, [value, motionValue]);
  useMotionValueEvent(springValue, 'change', (v) => {
    if (ref.current) ref.current.textContent = Math.round(v).toLocaleString('en-IN');
  });

  return <span ref={ref} style={{ ...S.numTab, ...style }}>0</span>;
}

/* ---------------------------------------------------------------------- */
/* Donut chart — adapted from animata/graphs/donut-chart.tsx: a single-     */
/* value SVG ring (stroke-dasharray/dashoffset) that animates in on mount   */
/* via a delayed state flip, exactly like the source. Used here as one      */
/* headline "reconciliation rate" metric instead of a multi-segment legend  */
/* pie — a clearer hero number for a SaaS dashboard card.                   */
/* ---------------------------------------------------------------------- */
function DonutChart({ size = 128, progress, strokeWidth = 13, trackColor = '#F1EEF6', progressColor = '#5C7E60', children }) {
  const [animate, setAnimate] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setAnimate(true), 250);
    return () => clearTimeout(t);
  }, []);
  const radius = size / 2 - strokeWidth / 2;
  const circumference = Math.PI * radius * 2;
  const offset = animate ? circumference * ((100 - progress) / 100) : circumference;

  return (
    <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ transform: 'rotate(-90deg)' }}>
        <circle r={radius} cx={size / 2} cy={size / 2} fill="transparent" stroke={trackColor} strokeWidth={strokeWidth} />
        <circle
          r={radius} cx={size / 2} cy={size / 2} fill="transparent" stroke={progressColor} strokeWidth={strokeWidth}
          strokeLinecap="round" strokeDasharray={`${circumference}px`} strokeDashoffset={`${offset}px`}
          style={{ transition: 'stroke-dashoffset 700ms cubic-bezier(0.34, 1.28, 0.64, 1)' }}
        />
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        {children}
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Status filter — adapted from animata/tabs/fluid-tabs.tsx: a shared       */
/* layoutId pill that physically slides/morphs between tabs (spring:        */
/* stiffness 380, damping 34), not a re-paint. Emoji per tab per the        */
/* user's explicit request for this control.                               */
/* ---------------------------------------------------------------------- */
const STATUS_TABS = [
  { key: 'All', emoji: '📋' },
  { key: 'Open', emoji: '⚠️' },
  { key: 'Resolved', emoji: '🗂️' },
  { key: 'Matched', emoji: '✔️' },
];
/* ---------------------------------------------------------------------- */
/* Growing search — adapted from cuicui's GrowingSearchVariant1: a pill that */
/* widens on focus and gives a brief confirm-glow on Enter. Search here is   */
/* already live-filtering on every keystroke (no submit step), so unlike     */
/* the reference this doesn't gate filtering behind Enter or add a toast     */
/* dependency (sonner) — the app already has its own toast host              */
/* (EditAccessToastHost) and a submit-only search would be a functional      */
/* downgrade from what this page already does. Just the grow + glow.         */
/* ---------------------------------------------------------------------- */
function GrowingSearch({ value, onChange, onExpandChange }) {
  const [focused, setFocused] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const expanded = focused || value.length > 0;

  // Reports expanded state upward so the header can slide the Upload/
  // Download buttons out of the way (see GRN_SEARCH_EXPANDED_WIDTH below) —
  // this search is anchored to the header's right edge and grows leftward,
  // so without that the expanding pill would just cover the buttons.
  useEffect(() => { onExpandChange && onExpandChange(expanded); }, [expanded, onExpandChange]);

  // Bug fix: at width:40 the fixed padding (24px) + icon (16px) + gap (8px)
  // already add up to 48px — more than the container itself — so the input
  // was being squeezed to zero/negative width with nothing left to click.
  // A <label> wrapping both (clicking anywhere in a label focuses its
  // descendant input, even the icon) plus an explicit 0-width on the input
  // itself when collapsed, instead of leaving it to flex distribution, fixes
  // both the missing click target and the arithmetic.
  return (
    <label className={`grn-grow-search${pulsing ? ' is-pulsing' : ''}`} style={{ ...S.growSearch, ...S.growSearchAnchored, width: expanded ? GRN_SEARCH_EXPANDED_WIDTH : GRN_SEARCH_COLLAPSED_WIDTH }}>
      <Search size={ICON.md} style={{ color: 'var(--grn-text-faint)', flex: 'none' }} />
      <input
        // Not type="search" — that renders as a native OS widget on Windows
        // Chrome/Edge (-webkit-appearance: searchfield) which kept painting
        // its own white box (clear-button well, focus fill, etc.) no matter
        // what `appearance`/`background` overrides were applied. This app
        // doesn't use any search-specific native behavior (Enter/clearing
        // are both handled here), so plain type="text" sidesteps the native
        // theming entirely instead of fighting it browser-by-browser.
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            setPulsing(true);
            setTimeout(() => setPulsing(false), 260);
            e.currentTarget.blur();
          }
        }}
        placeholder="Search PO, party, GRN…"
        style={{
          border: 'none', outline: 'none', boxShadow: 'none', minWidth: 0, fontSize: 13.5, color: 'var(--grn-text)', background: GROW_SEARCH_BG, fontFamily: "'Space Grotesk'",
          width: expanded ? '100%' : 0, flex: expanded ? 1 : '0 0 0', padding: 0, opacity: expanded ? 1 : 0, transition: 'opacity 0.2s, width 0.2s',
        }}
      />
    </label>
  );
}

/* ---------------------------------------------------------------------- */
/* Spinner — adapted from shadcn/ui's Spinner: a plain SVG ring rotated via  */
/* CSS, no dependency. Replaces the Download Images icon while zipping, and */
/* sits beside "Uploading…" on the Upload All button.                       */
/* ---------------------------------------------------------------------- */
function Spinner({ size = ICON.sm, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className="grn-spinner" style={{ flex: 'none' }} aria-hidden="true">
      <circle cx="12" cy="12" r="9" stroke={color} strokeWidth="3" strokeLinecap="round" strokeDasharray="34 100" opacity="0.9" />
    </svg>
  );
}

/* ---------------------------------------------------------------------- */
/* Pagination — adapted from shadcn/ui's Pagination: plain nav/button       */
/* markup (no separate primitives library), numbered pages with smart       */
/* ellipsis, active page in brand purple. Used above AND below the table,   */
/* both driven by the same TanStack Table pagination state so they can      */
/* never drift out of sync with each other or the table.                    */
/* ---------------------------------------------------------------------- */
function pageNumbersWithEllipsis(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [1];
  if (current > 3) pages.push('ellipsis-start');
  const start = Math.max(2, current - 1);
  const end = Math.min(total - 1, current + 1);
  for (let i = start; i <= end; i++) pages.push(i);
  if (current < total - 2) pages.push('ellipsis-end');
  pages.push(total);
  return pages;
}
function Pagination({ table }) {
  const pageCount = Math.max(1, table.getPageCount());
  const current = table.getState().pagination.pageIndex + 1;
  if (pageCount <= 1) return null;
  const pages = pageNumbersWithEllipsis(current, pageCount);
  return (
    <nav aria-label="Pagination" style={S.paginationNav}>
      <button type="button" className="grn-tab" style={{ ...S.paginationArrow, opacity: table.getCanPreviousPage() ? 1 : 0.4 }}
        onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>
        <ChevronLeft size={ICON.sm} />
      </button>
      {pages.map((p, i) => (
        typeof p === 'number' ? (
          <button key={p} type="button" className="grn-tab"
            style={{ ...S.paginationLink, ...(p === current ? S.paginationLinkActive : null) }}
            onClick={() => table.setPageIndex(p - 1)}
          >
            {p}
          </button>
        ) : (
          <span key={p + i} style={S.paginationEllipsis}>…</span>
        )
      ))}
      <button type="button" className="grn-tab" style={{ ...S.paginationArrow, opacity: table.getCanNextPage() ? 1 : 0.4 }}
        onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>
        <ChevronRight size={ICON.sm} />
      </button>
    </nav>
  );
}

/* ---------------------------------------------------------------------- */
/* Header background — adapted from animata/background/animated-beam.tsx:   */
/* fixed-width diagonal lanes, ~30% carrying a randomized "meteor" streak    */
/* on a loop (deterministic seeded-random, same technique as the source).   */
/* The original sits on a dark indigo hero gradient with light streaks —    */
/* this page stays on the light brand palette, so it's kept as a subtle     */
/* purple-on-white overlay behind the header's real content instead of a    */
/* literal dark-hero port. Scaled down (shorter streaks, fewer lanes) to    */
/* fit a ~70px header bar rather than a full hero section.                  */
/* ---------------------------------------------------------------------- */
const prand = (n) => {
  const x = Math.sin(n * 127.1 + 311.7) * 43758.5453;
  return x - Math.floor(x);
};
const lerp = (min, max, t) => min + (max - min) * t;
const HEADER_LANES = 40;

function HeaderBeam({ index }) {
  const fast = prand(index + 91) < 0.3;
  const duration = fast ? lerp(1.2, 3.2, prand(index)) : lerp(6, 14, prand(index));
  const delay = lerp(0, 9, prand(index + 13));
  const length = Math.round(lerp(10, 26, prand(index + 29)));
  const width = Math.max(2, Math.round(lerp(2, 4, prand(index + 53))));
  const opacity = fast ? lerp(0.32, 0.5, prand(index + 71)) : lerp(0.14, 0.32, prand(index + 71));
  return (
    <div
      className="grn-beam"
      style={{
        '--duration': `${duration.toFixed(2)}s`, '--delay': `${delay.toFixed(2)}s`,
        '--length': `${length}px`, '--opacity': opacity.toFixed(2), width: `${width}px`,
      }}
    />
  );
}
function HeaderBeamBackground() {
  return (
    <div className="grn-beam-bg" aria-hidden="true">
      {Array.from({ length: HEADER_LANES }, (_, i) => (
        <div key={i} className="grn-beam-lane">
          {prand(i + 7) < 0.3 && <HeaderBeam index={i + 1} />}
        </div>
      ))}
    </div>
  );
}

/* Dark-mode-only page background — adapted from animata/background/blurry-blob.tsx:
   large, heavily-blurred circles running a scale-pulse keyframe. The source uses
   mix-blend-mode: multiply against a light backdrop (darkens); inverted here to
   mix-blend-mode: screen against this page's dark backdrop so the blobs glow
   instead of vanishing. Fixed behind all page content, mounted only when dark
   (a discrete theme choice, not a media feature — CSS alone can't gate this). */
const BLOB_COLORS = ['#6E58A6', '#A48FD1', '#E08BA0'];
function GrnBlurryBlobBackground() {
  return (
    <div className="grn-blob-bg" aria-hidden="true">
      {BLOB_COLORS.map((color, i) => (
        <div key={color} className="grn-blob" style={{ background: color, animationDelay: `${i * 1.6}s` }} />
      ))}
    </div>
  );
}

function FluidStatusTabs({ value, onChange }) {
  return (
    <nav aria-label="Status filter" style={S.fluidNav}>
      <div role="tablist" style={S.fluidList}>
        {STATUS_TABS.map((opt) => {
          const active = opt.key === value;
          return (
            <button
              key={opt.key} type="button" role="tab" aria-selected={active} className="grn-tab"
              onClick={() => onChange(opt.key)} style={S.fluidTab}
            >
              {active && (
                <motion.span
                  layoutId="grn-status-pill" style={S.fluidPill}
                  transition={{ type: 'spring', stiffness: 380, damping: 34, mass: 0.75 }}
                />
              )}
              <span style={{ ...S.fluidTabContent, color: active ? '#fff' : 'var(--grn-text-muted)' }}>
                <span aria-hidden style={{ fontSize: 13, filter: 'grayscale(1)' }}>{opt.emoji}</span>
                {displayStatus(opt.key)}
              </span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/* react-day-picker's own dropdown caption (month/year, via captionLayout=
   "dropdown") renders a native <select> by default. Overriding it via the
   `components` prop with the exact same Base UI Menu popup as the standalone
   Month/Year filters below, per the user's request that they look identical.
   react-day-picker calls onChange with a native-event-shaped object
   ({ target: { value } }) — confirmed from its own DayPicker.js internals —
   so the Base UI radio selection is adapted back into that shape. */
function CalendarDropdown({ options, value, onChange, disabled }) {
  const current = options?.find(o => String(o.value) === String(value));
  return (
    <Menu.Root>
      <Menu.Trigger disabled={disabled} style={S.calendarCaptionTrigger}>
        {current?.label}
        <ChevronDown size={12} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={4} align="start" style={{ zIndex: 500 }}>
          <Menu.Popup style={S.menuPopup}>
            <Menu.RadioGroup value={String(value)} onValueChange={(v) => onChange({ target: { value: v } })}>
              {options?.map(o => (
                <Menu.RadioItem key={o.value} value={String(o.value)} disabled={o.disabled} style={S.menuItem}>{o.label}</Menu.RadioItem>
              ))}
            </Menu.RadioGroup>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

/* ---------------------------------------------------------------------- */
/* Date range picker — react-day-picker (the engine under shadcn/ui's       */
/* Calendar), captionLayout="dropdown" per the user's reference demo,       */
/* restyled to the portal palette via grn-calendar CSS below.               */
/* ---------------------------------------------------------------------- */
function DateRangePicker({ from, to, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const range = { from: from ? new Date(`${from}T00:00:00`) : undefined, to: to ? new Date(`${to}T00:00:00`) : undefined };
  const label = from || to
    ? `${from ? formatDate(from) : '…'} – ${to ? formatDate(to) : '…'}`
    : 'Date range';

  return (
    <div style={{ position: 'relative' }}>
      <button type="button" className="grn-tab" style={S.dateBtn} onClick={() => setOpen(o => !o)}>
        <CalendarIcon size={ICON.sm} />{label}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 499 }} />
          <div className="grn-calendar" style={S.calendarPopover}>
            <DayPicker
              mode="range"
              captionLayout="dropdown"
              components={{ Dropdown: CalendarDropdown }}
              selected={range}
              defaultMonth={range.from}
              numberOfMonths={2}
              onSelect={(r) => {
                // NOT .toISOString().slice(0,10) — that converts the clicked
                // day's LOCAL midnight to UTC, which rolls back to the
                // previous day in any timezone ahead of UTC (e.g. IST) —
                // exactly the "selects a day earlier" bug reported. date-fns
                // format() reads local y/m/d components directly, no UTC
                // conversion.
                onChange(r?.from ? formatDateFns(r.from, 'yyyy-MM-dd') : '', r?.to ? formatDateFns(r.to, 'yyyy-MM-dd') : '');
              }}
            />
            {(from || to) && (
              <button type="button" style={S.clearCalBtn} onClick={() => { onClear(); setOpen(false); }}>Clear dates</button>
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Month/Year quick filters — @base-ui-components/react Menu (what          */
/* shadcn/ui's DropdownMenu wraps), single-select via Menu.RadioGroup,      */
/* replacing the plain <select> per the user's reference demo.             */
/* ---------------------------------------------------------------------- */
function MonthYearMenu({ label, value, options, onChange }) {
  const current = options.find(o => o.value === value);
  return (
    <Menu.Root>
      <Menu.Trigger className="grn-tab" style={S.dateBtn}>
        {current ? current.label : label}
        <ChevronDown size={ICON.xs} />
      </Menu.Trigger>
      <Menu.Portal>
        <Menu.Positioner sideOffset={6} align="start" style={{ zIndex: 500 }}>
          <Menu.Popup style={S.menuPopup}>
            <Menu.RadioGroup value={value} onValueChange={onChange}>
              <Menu.RadioItem value="" style={S.menuItem}>Any {label.toLowerCase()}</Menu.RadioItem>
              {options.map(o => (
                <Menu.RadioItem key={o.value} value={o.value} style={S.menuItem}>{o.label}</Menu.RadioItem>
              ))}
            </Menu.RadioGroup>
          </Menu.Popup>
        </Menu.Positioner>
      </Menu.Portal>
    </Menu.Root>
  );
}

/* ---------------------------------------------------------------------- */
/* PO images — lazy-loads on panel open, same wh-po-images-* endpoints.     */
/* ---------------------------------------------------------------------- */
function PanelImages({ poNo, hasImage, maxVisible = 5 }) {
  const [images, setImages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [zipping, setZipping] = useState(false);

  useEffect(() => {
    setImages(null);
    if (!poNo || !hasImage) return;
    setLoading(true);
    fetch(`${API_BASE}/wh-po-images-get?po_number=${encodeURIComponent(poNo)}`)
      .then(res => res.json())
      .then(data => setImages(data.images || []))
      .catch(() => setImages([]))
      .finally(() => setLoading(false));
  }, [poNo, hasImage]);

  async function downloadAll() {
    if (!images || images.length === 0) return;
    setZipping(true);
    try {
      const res = await fetch(`${API_BASE}/wh-po-images-download-zip`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ po_numbers: [poNo] }),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to download images'));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${poNo}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { notifyEditBlocked(err.message || 'Failed to download images'); }
    setZipping(false);
  }

  if (!hasImage) return <div style={S.panelEmptyNote}>No box images uploaded for this PO.</div>;

  const visible = images ? images.slice(0, maxVisible) : [];
  const remaining = images ? images.length - visible.length : 0;

  return (
    <div style={{ width: '100%' }}>
      {loading && <div style={S.panelEmptyNote}>Loading images…</div>}
      {!loading && images && images.length === 0 && <div style={S.panelEmptyNote}>No images found.</div>}
      {!loading && images && images.length > 0 && (
        <>
          <div style={{ display: 'flex', marginBottom: 10 }}>
            {visible.map((img, i) => (
              <a
                key={img.id}
                href={img.view_url || undefined}
                target="_blank"
                rel="noreferrer"
                style={{ marginLeft: i === 0 ? 0 : -16, position: 'relative', zIndex: visible.length - i }}
              >
                <img src={img.view_url} alt={img.blob_name} style={S.thumbStacked} />
              </a>
            ))}
            {remaining > 0 && (
              <div style={{ ...S.thumbStacked, ...S.imagePlusTile, marginLeft: -16, position: 'relative', zIndex: 0 }}>+{remaining}</div>
            )}
          </div>
          <button type="button" style={S.btnGhostSmall} onClick={downloadAll} disabled={zipping}>
            <Download size={ICON.xs} />{zipping ? 'Zipping…' : 'Download all'}
          </button>
        </>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Delivery PODs — lazy-loads on panel open, same SAS-URL pattern as        */
/* PanelImages but sourced from Shiprocket MIS via grn-management?action=pod */
/* ---------------------------------------------------------------------- */
function formatPodSize(bytes) {
  if (!bytes && bytes !== 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  return `${Math.round(bytes / 1024)} KB`;
}

function PanelPod({ poNo }) {
  const [pods, setPods] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPods(null);
    if (!poNo) return;
    setLoading(true);
    fetch(`${API_BASE}/grn-management?action=pod&po_no=${encodeURIComponent(poNo)}`)
      .then(res => res.json())
      .then(data => setPods(data.pods || []))
      .catch(() => setPods([]))
      .finally(() => setLoading(false));
  }, [poNo]);

  if (loading) return <div style={S.panelEmptyNote}>Loading POD…</div>;
  if (!pods || pods.length === 0) return <div style={S.panelEmptyNote}>No POD details yet.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, width: '100%' }}>
      {pods.map(p => {
        const meta = [p.file_type ? p.file_type.toUpperCase() : null, p.page_count ? `${p.page_count} page${p.page_count === 1 ? '' : 's'}` : null, formatPodSize(p.size_bytes)]
          .filter(Boolean).join(' · ');
        return (
          <div key={p.waybill_no} style={S.podCard}>
            <div style={S.podInfoRow}>
              <FileText size={ICON.md} style={{ flexShrink: 0, color: 'var(--grn-text-muted)' }} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--grn-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {p.invoice_no || p.waybill_no}
                </div>
                {meta && <div style={{ fontSize: 11.5, color: 'var(--grn-text-muted)', marginTop: 1 }}>{meta}</div>}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <a href={p.view_url} target="_blank" rel="noreferrer" style={S.podOpenBtn}>Open POD</a>
              <PodInvoiceButtons invoiceNo={p.invoice_no} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* Lazy-fetches the invoice PDF's SAS view URL for a POD's linked invoice
   (same invoice-attachments container the Invoices page reads via
   acc-invoices?action=invoice_pdf) and renders an "Open Invoice" button
   alongside the POD buttons once it resolves. Renders nothing when the
   invoice has no PDF on file, so a missing attachment doesn't leave a dead
   button in the card. */
function PodInvoiceButtons({ invoiceNo }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setPdfUrl(null);
    if (!invoiceNo) return;
    setLoading(true);
    fetch(`${API_BASE}/acc-invoices?action=invoice_pdf&invoice_no=${encodeURIComponent(invoiceNo)}`)
      .then(res => res.json())
      .then(data => setPdfUrl(data.pdf_url || null))
      .catch(() => setPdfUrl(null))
      .finally(() => setLoading(false));
  }, [invoiceNo]);

  if (loading || !pdfUrl) return null;

  return <a href={pdfUrl} target="_blank" rel="noreferrer" style={S.podOpenBtnAlt}>Open Invoice</a>;
}

/* ---------------------------------------------------------------------- */
/* Quick CN/DN entry, directly in the main table. Only offered when a PO    */
/* has exactly one GRN — a PO with several GRNs has one CN/DN per GRN, so   */
/* that case stays on the existing per-GRN editor in the detail panel.      */
/* Saving routes through the same `onSave`/`updateRow` path as that panel,  */
/* which is also where the CN/DN-entered -> status=Resolved rule lives.     */
/* ---------------------------------------------------------------------- */
function CnDnCell({ group, user, onSave }) {
  const canEdit = hasEditAccess(user, 'po_so_lifecycle', 'grn_management');
  const single = group.grns.length === 1;
  const grn = single ? group.grns[0] : null;

  // Hooks must run unconditionally (same order every render) regardless of
  // `single` — the multi-GRN early-out happens in the render below instead.
  const [editing, setEditing] = useState(single && !grn.cn_dn_no);
  const [type, setType] = useState(single ? (grn.cn_dn_type || '') : '');
  const [no, setNo] = useState(single ? (grn.cn_dn_no || '') : '');

  // Resyncs when the saved value changes from elsewhere (e.g. the detail
  // panel's own CN/DN editor, or a refetch) rather than from this cell.
  useEffect(() => {
    if (!single) return;
    setType(grn.cn_dn_type || '');
    setNo(grn.cn_dn_no || '');
    if (grn.cn_dn_no) setEditing(false);
  }, [single, grn?.cn_dn_type, grn?.cn_dn_no]);

  if (!single) {
    return <span style={{ color: 'var(--grn-border-soft)' }}>—</span>;
  }

  async function save() {
    const trimmed = no.trim();
    await onSave(grn.id, { cn_dn_type: trimmed ? type : '', cn_dn_no: trimmed });
    setEditing(false);
  }

  // One control at a time — picking a type reveals the number input in its
  // place, instead of showing both side by side, so this column stays as
  // narrow as a normal badge column instead of pushing later columns (this
  // is what originally forced "Ticket" onto its own line: two controls side
  // by side blew out the column's width).
  if (editing && canEdit && !type) {
    return (
      <div onClick={(e) => e.stopPropagation()}>
        <select value={type} onChange={(e) => setType(e.target.value)} style={{ ...S.select, width: '100%', padding: '6px 24px 6px 10px', fontSize: 12 }}>
          <option value="">— Select —</option>
          <option value="Credit Note">Credit Note</option>
          <option value="Debit Note">Debit Note</option>
        </select>
      </div>
    );
  }

  if (editing && canEdit) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} onClick={(e) => e.stopPropagation()}>
        <input type="text" value={no} placeholder="Note no." autoFocus
          onChange={(e) => setNo(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } if (e.key === 'Escape') { e.preventDefault(); setType(''); } }}
          style={{ ...S.ticketInput, flex: 1, padding: '6px 10px' }} />
        <span onClick={save} style={{ ...S.copyIcon, color: 'var(--grn-green)' }} title="Save">
          <Check size={ICON.sm} />
        </span>
        <span onClick={() => setType('')} style={S.copyIcon} title="Change type">
          <X size={ICON.sm} />
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }} onClick={(e) => e.stopPropagation()}>
      {grn.cn_dn_type && grn.cn_dn_no ? (
        <span style={{
          ...S.statusBadge,
          background: grn.cn_dn_type === 'Credit Note' ? '#E0F2FE' : '#FFEDD5',
          color: grn.cn_dn_type === 'Credit Note' ? '#075985' : '#9A3412',
          borderColor: grn.cn_dn_type === 'Credit Note' ? '#BAE6FD' : '#FED7AA',
        }}>
          {grn.cn_dn_type === 'Credit Note' ? 'CN' : 'DN'} {grn.cn_dn_no}
        </span>
      ) : (
        <span style={{ color: 'var(--grn-border-soft)' }}>—</span>
      )}
      {canEdit && (
        <span onClick={() => setEditing(true)} style={S.copyIcon} title="Edit">
          <Pencil size={ICON.xs} />
        </span>
      )}
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* One GRN row inside the right column's detail view — click to expand      */
/* its editor in place.                                                     */
/* ---------------------------------------------------------------------- */
function PanelGrnRow({ row, user, onSave }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(row.status);
  const [tickets, setTickets] = useState(parseTickets(row.ticket_no));
  const [ticketInput, setTicketInput] = useState('');
  const [openDate, setOpenDate] = useState(toDateInputValue(row.ticket_open_date));
  const [closeDate, setCloseDate] = useState(toDateInputValue(row.ticket_close_date));
  const [remark, setRemark] = useState(row.remark || '');
  const [cnDnType, setCnDnType] = useState(row.cn_dn_type || '');
  const [cnDnNo, setCnDnNo] = useState(row.cn_dn_no || '');
  const [saving, setSaving] = useState(false);
  const st = statusStyle(row.status);

  function toggle() {
    if (!hasEditAccess(user, 'po_so_lifecycle', 'grn_management')) {
      if (!open) { notifyEditBlocked('View-only access — you can look, but not edit this GRN.'); }
    }
    setOpen(o => !o);
  }

  function addTicket() {
    if (!ticketInput.trim()) return;
    setTickets(prev => [...prev, ticketInput.trim()]);
    setOpenDate(prev => prev || todayISO());
    setTicketInput('');
  }
  function removeTicket(idx) { setTickets(prev => prev.filter((_, i) => i !== idx)); }
  function toggleNoTicket() {
    setTickets(prev => (prev.length === 1 && prev[0] === NO_TICKET_LABEL) ? [] : [NO_TICKET_LABEL]);
    setTicketInput('');
  }

  async function handleSave() {
    if (!hasEditAccess(user, 'po_so_lifecycle', 'grn_management')) return notifyEditBlocked();
    setSaving(true);
    await onSave(row.id, {
      status,
      ticket_no: tickets.join(','),
      ticket_open_date: openDate || null,
      ticket_close_date: closeDate || null,
      remark: remark.trim(),
      cn_dn_type: cnDnType,
      cn_dn_no: cnDnNo.trim(),
    });
    setSaving(false);
    setOpen(false);
  }

  return (
    <div style={S.panelGrnRow}>
      <div style={{ ...S.panelGrnHead, flexDirection: 'column', alignItems: 'stretch', gap: 6 }} onClick={toggle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ fontWeight: 600, color: 'var(--grn-text)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.grn_no || undefined}>
            {row.grn_no || '(no GRN no.)'}
          </span>
          {row.grn_no && (
            <span onClick={(e) => { e.stopPropagation(); copyToClipboard(row.grn_no, () => notifyEditBlocked('GRN No. copied!')); }} style={S.copyIcon}>
              <Copy size={ICON.xs - 1} />
            </span>
          )}
          <span style={{ flex: 1 }} />
          <ChevronDown size={ICON.sm} style={{ flex: 'none', color: 'var(--grn-text-faint)', transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
          <span style={{ color: 'var(--grn-text-faint)', fontSize: 12, flex: 'none' }}>{formatDate(row.grn_date)}</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
            <span style={{ ...S.numTab, fontWeight: 700, color: 'var(--grn-text)' }}>{row.grn_qty ?? '—'}</span>
            <span style={{ ...S.statusBadge, background: st.bg, color: st.color, borderColor: st.border }}>{displayStatus(row.status)}</span>
            {parseTickets(row.ticket_no).length > 0 && <TicketIcon size={ICON.sm} style={{ color: 'var(--grn-purple)' }} />}
            {row.cn_dn_type && (
              <span style={{
                ...S.statusBadge,
                background: row.cn_dn_type === 'Credit Note' ? '#E0F2FE' : '#FFEDD5',
                color: row.cn_dn_type === 'Credit Note' ? '#075985' : '#9A3412',
                borderColor: row.cn_dn_type === 'Credit Note' ? '#BAE6FD' : '#FED7AA',
              }}>
                {row.cn_dn_type === 'Credit Note' ? 'CN' : 'DN'} {row.cn_dn_no || ''}
              </span>
            )}
          </div>
        </div>
        {row.remark && (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 5, minWidth: 0, color: 'var(--grn-amber)', fontSize: 11.5 }}>
            <StickyNote size={ICON.xs} style={{ flex: 'none', marginTop: 2 }} />
            <span style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{row.remark}</span>
          </div>
        )}
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 38 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={S.panelGrnBody}>
              <div>
                <div style={S.fieldLabel}>Status</div>
                <select value={status} onChange={(e) => setStatus(e.target.value)} style={{ ...S.select, width: '100%' }}>
                  <option value="Open">Discrepancies</option>
                  <option value="Resolved">Resolved</option>
                  <option value="Matched">Matched</option>
                </select>
              </div>

              <div>
                <div style={S.fieldLabel}>Ticket No.</div>
                {tickets.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                    {tickets.map((t, i) => (
                      <span key={i} style={S.ticketChip}>
                        {t}<span onClick={() => removeTicket(i)} style={{ marginLeft: 6, cursor: 'pointer', fontWeight: 700 }}>×</span>
                      </span>
                    ))}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8 }}>
                  <input type="text" value={ticketInput} placeholder="Add ticket no. + Enter"
                    disabled={tickets.length === 1 && tickets[0] === NO_TICKET_LABEL}
                    onChange={(e) => setTicketInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTicket(); } }}
                    style={{ ...S.ticketInput, flex: 1 }} />
                  <button type="button" onClick={toggleNoTicket}
                    style={{
                      ...S.btnGhostSmall, flexShrink: 0,
                      ...(tickets.length === 1 && tickets[0] === NO_TICKET_LABEL
                        ? { background: 'var(--grn-purple)', color: '#fff', borderColor: 'var(--grn-purple)' }
                        : {}),
                    }}>
                    No Ticket
                  </button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={S.fieldLabel}>Ticket Open Date</div>
                  <input type="date" value={openDate} onChange={(e) => setOpenDate(e.target.value)} style={S.ticketInput} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={S.fieldLabel}>Ticket Close Date</div>
                  <input type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} style={S.ticketInput} />
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={S.fieldLabel}>Credit/Debit Note</div>
                  <select value={cnDnType} onChange={(e) => setCnDnType(e.target.value)} style={{ ...S.select, width: '100%' }}>
                    <option value="">— None —</option>
                    <option value="Credit Note">Credit Note</option>
                    <option value="Debit Note">Debit Note</option>
                  </select>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={S.fieldLabel}>Note No.</div>
                  <input type="text" value={cnDnNo} onChange={(e) => setCnDnNo(e.target.value)}
                    placeholder="Enter note number" style={S.ticketInput} />
                </div>
              </div>

              <div>
                <div style={S.fieldLabel}>Remark</div>
                <textarea value={remark} onChange={(e) => setRemark(e.target.value)} rows={3}
                  placeholder="Add a remark for this GRN…" style={{ ...S.ticketInput, resize: 'vertical' }} />
              </div>

              <button type="button" style={S.btnPrimary} onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Right column — KPI view (default) and PO detail view (swapped in when    */
/* a row is selected). Same column, same width, content crossfades —       */
/* never an overlay/drawer.                                                 */
/* ---------------------------------------------------------------------- */
/* Bento-grid layout adapted from animata/bento-grid/eight.tsx: an
   asymmetric mix of full-width "banner" cards and half-width cards instead
   of a uniform grid. The original is a 4-column marketing showcase with
   arbitrary yellow/violet/orange cards; this column is only ~280-380px
   wide (too narrow for 4 columns) so it becomes a 2-column bento, and every
   card reuses this page's own already-established stat colors (amber for
   Discrepancies, blue for Matched, green for Resolved) rather than
   inventing new ones. */
function KpiView({ dateFiltered, statusCounts }) {
  const total = dateFiltered.length;
  const healthy = (statusCounts.Matched || 0) + (statusCounts.Resolved || 0);
  const pct = total ? Math.round((healthy / total) * 100) : 0;
  return (
    <motion.div key="kpi" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
      <div style={S.bentoGrid}>
        {/* Row 1 — hero cell: the reconciliation ring, full width */}
        <div style={{ ...S.bentoCard, ...S.bentoSpan2, alignItems: 'center', textAlign: 'center', background: 'var(--grn-surface-subtle)' }}>
          <DonutChart size={112} progress={pct} progressColor="var(--grn-green)" trackColor="var(--grn-border)">
            <span style={{ ...S.numTab, fontSize: 22, fontWeight: 700, color: 'var(--grn-text)' }}>{pct}%</span>
          </DonutChart>
          <div style={{ fontSize: 11.5, color: 'var(--grn-text-muted)', fontWeight: 600, marginTop: 8 }}>Reconciled (Matched or Resolved)</div>
        </div>

        {/* Row 2 — headline stat banner, full width, dark like FeatureTwo's violet banner */}
        <div style={{ ...S.bentoCard, ...S.bentoSpan2, background: 'var(--grn-accent-surface)' }}>
          <div style={{ fontSize: 10.5, color: '#B9AEDD', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Total GRNs</div>
          <Counter value={total} style={{ fontSize: 30, fontWeight: 700, color: '#fff' }} />
        </div>

        {/* Row 3 — two half-width cards */}
        <div style={{ ...S.bentoCard, background: 'var(--grn-amber-tint)' }}>
          <div style={{ ...S.statLabel, color: 'var(--grn-amber)' }}>Discrepancies</div>
          <Counter value={statusCounts.Open || 0} style={{ fontSize: 22, fontWeight: 700, color: 'var(--grn-amber)' }} />
        </div>
        <div style={{ ...S.bentoCard, background: 'var(--grn-blue-tint)' }}>
          <div style={{ ...S.statLabel, color: 'var(--grn-blue)' }}>Matched</div>
          <Counter value={statusCounts.Matched || 0} style={{ fontSize: 22, fontWeight: 700, color: 'var(--grn-blue)' }} />
        </div>

        {/* Row 4 — Resolved banner, full width */}
        <div style={{ ...S.bentoCard, ...S.bentoSpan2, background: 'var(--grn-green-tint)' }}>
          <div style={{ ...S.statLabel, color: 'var(--grn-green)' }}>Resolved</div>
          <Counter value={statusCounts.Resolved || 0} style={{ fontSize: 22, fontWeight: 700, color: 'var(--grn-green)' }} />
        </div>
      </div>
    </motion.div>
  );
}

/* Upload File now opens in the same persistent right column as the KPI/
   detail views, instead of a centered modal overlay — same crossfade
   pattern, same "Back" affordance, nothing floating on top of the page. */
function UploadView({ vendorFiles, uploading, uploadSummary, onFile, onUploadAll, onBack }) {
  return (
    <motion.div key="upload" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
      <button type="button" onClick={onBack} style={S.backBtn}><ArrowLeft size={ICON.sm} />Back to overview</button>

      <div style={S.panelHeader}>
        <div style={{ fontFamily: "'Space Grotesk'", fontSize: 18, fontWeight: 600, color: 'var(--grn-text)' }}>Upload GRN File</div>
        <div style={{ fontSize: 12.5, color: 'var(--grn-text-muted)', marginTop: 2 }}>Select a vendor and upload their GRN sheet.</div>
      </div>

      <div style={{ ...S.bentoGrid, padding: '0 20px 20px' }}>
        {VENDOR_NAMES.map((name, i) => {
          const entry = vendorFiles[name];
          const vs = VENDOR_STYLE[i];
          const uploaded = !!entry?.fileName && !entry?.error;
          return (
            <div key={name} style={{ ...S.bentoCard, ...(uploaded ? S.bentoSpan2 : null), background: vs.tint, flexDirection: 'row', alignItems: 'center', gap: 10 }}>
              <div style={{ ...S.vendorAvatar, width: 34, height: 34, fontSize: 12.5, background: 'var(--grn-surface)', color: vs.color, flex: 'none' }}>
                {name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: vs.color }}>{name}</div>
                {entry?.error ? (
                  <div style={{ fontSize: 11, color: 'var(--grn-error)', fontWeight: 500, marginTop: 2 }}>{entry.error}</div>
                ) : entry?.fileName ? (
                  <div style={{ fontSize: 11, color: 'var(--grn-green)', fontWeight: 500, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Check size={ICON.xs - 2} />{entry.fileName} ({entry.rows?.length ?? 0} rows)
                  </div>
                ) : (
                  <div style={{ fontSize: 11, color: 'var(--grn-text-muted)', marginTop: 2 }}>No file selected</div>
                )}
              </div>
              <label style={{ ...S.chooseFileCompact, background: 'var(--grn-surface)', color: vs.color, flex: 'none' }}>
                <Upload size={ICON.xs - 1} />
                <input type="file" accept=".csv,.xls,.xlsx" style={{ display: 'none' }} onChange={(e) => e.target.files[0] && onFile(name, e.target.files[0])} />
              </label>
            </div>
          );
        })}
      </div>

      {uploadSummary && (
        <div style={{ margin: '0 20px 16px', padding: '10px 14px', borderRadius: 10, background: '#F6F2FB', color: '#4C3B78', fontSize: 12 }}>{uploadSummary}</div>
      )}

      <div style={{ padding: '0 20px 20px' }}>
        <button onClick={onUploadAll} style={{ ...S.btnPrimary, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }} disabled={uploading}>
          {uploading && <Spinner size={ICON.sm} color="#fff" />}
          {uploading ? 'Uploading…' : 'Upload All'}
        </button>
      </div>
    </motion.div>
  );
}

function DetailView({ group, hasImage, user, onSave, onBack }) {
  const diffColor = group.diff === null || group.diff === 0 ? 'var(--grn-text-faint)' : 'var(--grn-error)';
  const tickets = allTicketsForGroup(group);
  return (
    <motion.div key="detail" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
      <button type="button" onClick={onBack} style={S.backBtn}><ArrowLeft size={ICON.sm} />Back to overview</button>

      <div style={S.panelHeader}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: "'Space Grotesk'", fontSize: 18, fontWeight: 600, color: 'var(--grn-text)' }}>{group.poNo || '—'}</span>
            {group.poNo && (
              <span onClick={() => copyToClipboard(group.poNo, () => notifyEditBlocked('PO Number copied!'))} style={S.copyIcon}><Copy size={ICON.sm} /></span>
            )}
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--grn-text-muted)', marginTop: 2 }}>{group.partyNames.join(', ') || '—'} · PO Date {formatDate(group.poDate)}</div>
        </div>
      </div>

      <div style={{ ...S.bentoGrid, padding: '0 20px 20px' }}>
        <div style={{ ...S.bentoCard, background: 'var(--grn-blue-tint)', alignItems: 'center', textAlign: 'center' }}>
          <div style={{ ...S.statLabel, color: 'var(--grn-blue)' }}>Invoice Qty</div>
          <div style={{ ...S.numTab, fontSize: 18, fontWeight: 700, color: 'var(--grn-blue)' }}>{group.invoiceQty ?? '—'}</div>
        </div>
        <div style={{ ...S.bentoCard, background: 'var(--grn-green-tint)', alignItems: 'center', textAlign: 'center' }}>
          <div style={{ ...S.statLabel, color: 'var(--grn-green)' }}>GRN Qty</div>
          <div style={{ ...S.numTab, fontSize: 18, fontWeight: 700, color: 'var(--grn-green)' }}>{group.totalGrnQty}</div>
        </div>
        <div style={{ ...S.bentoCard, background: group.diff ? 'var(--grn-pink-tint)' : 'var(--grn-surface-subtle)', alignItems: 'center', textAlign: 'center', minHeight: Math.round(76 * 1.1) }}>
          <div style={{ ...S.statLabel, color: diffColor }}>Diff</div>
          <div className={group.diff ? 'grn-diff-pulse' : ''} style={{ ...S.numTab, fontSize: 24, fontWeight: 700, color: diffColor }}>{group.diff ?? '—'}</div>
        </div>

        <div style={{ ...S.bentoCard, background: 'var(--grn-surface-subtle)', alignItems: 'flex-start', minHeight: Math.round(76 * 1.1) }}>
          <div style={{ ...S.statLabel, color: 'var(--grn-text-muted)' }}>Box images</div>
          <PanelImages poNo={group.poNo} hasImage={hasImage} maxVisible={3} />
        </div>

        <div style={{ ...S.bentoCard, ...S.bentoSpan2, background: 'var(--grn-surface-subtle)', alignItems: 'flex-start' }}>
          <div style={{ ...S.statLabel, color: 'var(--grn-text-muted)' }}>POD</div>
          <PanelPod poNo={group.poNo} />
        </div>

        {tickets.length > 0 && (
          <div style={{ ...S.bentoCard, ...S.bentoSpan2, background: 'var(--grn-purple-tint)', alignItems: 'flex-start' }}>
            <div style={{ ...S.statLabel, color: 'var(--grn-purple)', display: 'flex', alignItems: 'center', gap: 5 }}>
              <TicketIcon size={ICON.xs} />Ticket{tickets.length === 1 ? '' : 's'} ({tickets.length})
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {tickets.map((t, i) => (
                <span
                  key={i}
                  title="Click to copy"
                  onClick={() => copyToClipboard(t, () => notifyEditBlocked(`Ticket ${t} copied!`))}
                  style={{ ...S.ticketChip, fontSize: 13.5, cursor: 'pointer' }}
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}

        <div style={{ ...S.bentoCard, ...S.bentoSpan2, background: 'var(--grn-surface-subtle)', alignItems: 'stretch' }}>
          <div style={{ ...S.statLabel, color: 'var(--grn-text-muted)' }}>{group.grns.length} GRN{group.grns.length === 1 ? '' : 's'} under this PO</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {group.grns.map(r => <PanelGrnRow key={r.id} row={r} user={user} onSave={onSave} />)}
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ---------------------------------------------------------------------- */
/* Component                                                               */
/* ---------------------------------------------------------------------- */
export default function GRNManagement({ user, theme }) {
  const dark = theme === 'dark';
  // Mirrored onto <html> (not just this page's own root div) because Base
  // UI's Menu.Portal renders dropdown popups onto document.body — a :root
  // attribute is visible to that portaled content via normal CSS variable
  // inheritance; an attribute on our own div wouldn't be, since a portal
  // breaks DOM (not React-tree) ancestry. Cleared on unmount so leaving this
  // page can't leave the whole document flagged dark.
  useEffect(() => {
    const root = document.documentElement;
    if (dark) root.setAttribute('data-grn-theme', 'dark'); else root.removeAttribute('data-grn-theme');
    return () => root.removeAttribute('data-grn-theme');
  }, [dark]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [statusFilter, setStatusFilter] = useState('Open');
  const [uploadOpen, setUploadOpen] = useState(false);
  const [vendorFiles, setVendorFiles] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadSummary, setUploadSummary] = useState('');
  const [poNumbersWithImages, setPoNumbersWithImages] = useState(new Set());
  const [podZipping, setPodZipping] = useState(false);
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('');
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 50 });
  const [selectedKey, setSelectedKey] = useState(null);
  const [searchExpanded, setSearchExpanded] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search).get('q');
    if (q) setSearch(q);

    // Deep link from a discrepancy alert email (?po=...) — open straight to
    // that PO's detail view. 'All' status + the search box pre-filled with
    // the PO number both guarantee it's actually visible regardless of
    // whatever the default 'Open' status filter would otherwise hide (date
    // range needs no reset: empty dateFrom/dateTo already means "no limit").
    const po = new URLSearchParams(window.location.search).get('po');
    if (po) {
      setSearch(po);
      setStatusFilter('All');
    }
  }, []);

  const deepLinkPoRef = useRef(false);

  const loadRows = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await fetch(`${API_BASE}/grn-management?action=list`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to load GRNs');

      const rawRows = data.rows || [];
      const byPo = groupRowsByPo(rawRows);
      const pendingIds = [];
      const withAutoStatus = rawRows.map(r => {
        const grns = byPo.get(groupKeyForRow(r)) || [r];
        const { diff } = aggregateGroup(grns);
        if (diff === 0 && r.status !== 'Matched') {
          pendingIds.push(r.id);
          return { ...r, status: 'Matched' };
        }
        return r;
      });
      pendingIds.forEach(id => {
        fetch(`${API_BASE}/grn-management?action=update`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, status: 'Matched' }),
        }).catch(() => {});
      });
      setRows(withAutoStatus);
    } catch (err) { setLoadError(err.message); }
    setLoading(false);
  }, []);

  useEffect(() => { loadRows(); }, [loadRows]);

  useEffect(() => {
    fetch(`${API_BASE}/wh-po-images-get?list=po_numbers`)
      .then(res => res.json())
      .then(data => setPoNumbersWithImages(new Set((data.po_numbers || []).map(p => String(p).trim().toUpperCase()))))
      .catch(() => {});
  }, []);

  const updateRow = async (id, patch) => {
    // Mirrors the backend's rule (grn-management.js PUT ?action=update) so the
    // optimistic local state — and which status tab the row shows under —
    // updates instantly instead of waiting for a refetch.
    if ('cn_dn_no' in patch) {
      const trimmed = (patch.cn_dn_no || '').trim();
      patch = { ...patch, status: trimmed ? 'Resolved' : 'Open' };
    }
    setRows(prev => prev.map(r => (r.id === id ? { ...r, ...patch } : r)));
    try {
      await fetch(`${API_BASE}/grn-management?action=update`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id, ...patch }),
      });
    } catch { /* optimistic update stays; next refresh reconciles */ }
  };

  const handleVendorFile = async (vendor, file) => {
    const parser = VENDOR_PARSERS[vendor];
    if (!parser || !file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = window.XLSX.read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rawRows = window.XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
      const headerRowIdx = rawRows.findIndex(r => {
        const cells = new Set(r.map(cellToHeaderKey));
        return parser.requiredColumns.every(c => cells.has(c));
      });
      if (headerRowIdx === -1) {
        setVendorFiles(prev => ({ ...prev, [vendor]: { fileName: file.name, error: `Wrong file format for ${vendor}. Expected columns: ${parser.requiredColumns.join(', ')}.` } }));
        return;
      }
      const headerRow = rawRows[headerRowIdx].map(cellToHeaderKey);
      const dataRows = rawRows.slice(headerRowIdx + 1).filter(r => r.some(c => String(c).trim() !== ''));
      const normalized = dataRows.map(r => {
        const obj = {};
        headerRow.forEach((key, i) => { if (key) obj[key] = r[i]; });
        return obj;
      });
      const filtered = parser.filter ? normalized.filter(parser.filter) : normalized;
      let mapped = filtered.map(r => ({ ...parser.map(r), party_name: vendor }));
      // Vendors whose export is itemized per SKU line (several raw rows
      // sharing one GRN) declare aggregateBy so those lines' quantities get
      // summed into a single row per GRN — matching this table's one-row-
      // per-GRN model — instead of being uploaded as separate rows that
      // would just overwrite each other.
      if (parser.aggregateBy) {
        const groups = new Map();
        for (const row of mapped) {
          const key = parser.aggregateBy(row);
          if (!key) continue;
          if (!groups.has(key)) groups.set(key, { ...row, grn_qty: 0 });
          groups.get(key).grn_qty += Number(row.grn_qty) || 0;
        }
        mapped = [...groups.values()];
      }
      setVendorFiles(prev => ({ ...prev, [vendor]: { fileName: file.name, rows: mapped, error: null } }));
    } catch (err) {
      setVendorFiles(prev => ({ ...prev, [vendor]: { fileName: file.name, error: 'Could not read file: ' + err.message } }));
    }
  };

  const handleUploadAll = async () => {
    const ready = Object.entries(vendorFiles).filter(([, v]) => v.rows && v.rows.length > 0);
    if (ready.length === 0) { setUploadOpen(false); return; }
    setUploading(true);
    const summaries = [];
    for (const [vendor, data] of ready) {
      try {
        const res = await fetch(`${API_BASE}/grn-management?action=upload`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ vendor, rows: data.rows }),
        });
        const json = await res.json();
        if (!res.ok) throw new Error(json.message || 'Upload failed');
        summaries.push(`${vendor}: ${json.inserted} new, ${json.updated} updated`);
      } catch (err) { summaries.push(`${vendor}: failed — ${err.message}`); }
    }
    setUploading(false);
    setUploadSummary(summaries.join('  •  '));
    setVendorFiles({});
    await loadRows();
  };

  const closeUploadModal = () => { setUploadOpen(false); setUploadSummary(''); setVendorFiles({}); };

  function applyMonthYear(month, year) {
    if (!month && !year) return;
    const y = year || CURRENT_YEAR;
    // formatDateFns, not .toISOString() — same local-midnight-to-UTC
    // rollback bug as the calendar's onSelect above.
    if (month) {
      setDateFrom(formatDateFns(new Date(y, month - 1, 1), 'yyyy-MM-dd'));
      setDateTo(formatDateFns(new Date(y, month, 0), 'yyyy-MM-dd'));
    } else {
      setDateFrom(formatDateFns(new Date(y, 0, 1), 'yyyy-MM-dd'));
      setDateTo(formatDateFns(new Date(y, 11, 31), 'yyyy-MM-dd'));
    }
  }
  function clearDateRange() { setDateFrom(''); setDateTo(''); setSelectedMonth(''); setSelectedYear(''); }

  // Memoized so this chain — and everything derived from it (visibleGroups,
  // and the `data` array handed to useReactTable) — only recomputes when one
  // of these actual inputs changes, not on every render. Without this, each
  // was a brand-new array every render, which meant TanStack Table's data
  // prop was a new reference every render too — forcing it to re-sort and
  // re-paginate the full ~1,150-row dataset on every single render instead
  // of only when the filters/data actually changed.
  const statusFiltered = useMemo(() =>
    statusFilter === 'All' ? rows : rows.filter(r => r.status === statusFilter),
    [rows, statusFilter]);

  const q = search.trim().toLowerCase();
  const searchFiltered = useMemo(() => !q ? statusFiltered : statusFiltered.filter(r =>
    (r.po_no || '').toLowerCase().includes(q) || (r.party_name || '').toLowerCase().includes(q) || (r.grn_no || '').toLowerCase().includes(q)),
    [statusFiltered, q]);

  const dateFiltered = useMemo(() => (!dateFrom && !dateTo) ? searchFiltered : searchFiltered.filter(r => {
    if (!r.po_date) return false;
    const d = toDateInputValue(r.po_date);
    if (!d) return false;
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  }), [searchFiltered, dateFrom, dateTo]);

  const groupsByPo = useMemo(() => groupRowsByPo(rows), [rows]);
  const visibleGroupKeys = useMemo(() => {
    const keys = new Set();
    dateFiltered.forEach(r => keys.add(groupKeyForRow(r)));
    return keys;
  }, [dateFiltered]);
  const visibleGroups = useMemo(() =>
    [...visibleGroupKeys].map(key => ({ key, ...aggregateGroup(groupsByPo.get(key) || []) })),
    [visibleGroupKeys, groupsByPo]);

  const statusCounts = useMemo(() => dateFiltered.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {}), [dateFiltered]);

  const selectedGroup = selectedKey ? visibleGroups.find(g => g.key === selectedKey) : null;

  // Second half of the ?po= deep link (see the mount effect above): once
  // visibleGroups actually contains the target PO, select it — runs once
  // per page load (deepLinkPoRef), so it doesn't fight a user who manually
  // deselects afterward on a later visibleGroups recompute.
  useEffect(() => {
    if (deepLinkPoRef.current) return;
    const po = new URLSearchParams(window.location.search).get('po');
    if (!po || visibleGroups.length === 0) return;
    const key = po.trim().toUpperCase();
    if (visibleGroups.some(g => g.key === key)) {
      setSelectedKey(key);
      deepLinkPoRef.current = true;
    }
  }, [visibleGroups]);

  function exportExcel() {
    const XLSX = window.XLSX;
    if (!XLSX) { alert('SheetJS not loaded'); return; }
    // Flat: one row per GRN, with the PO-level fields repeated on every GRN
    // under that PO. A spreadsheet needs one complete record per row to be
    // filterable/summable — splitting a record's fields across two rows
    // (the previous "PO summary row" + "GRN detail row" pattern) produced a
    // file no spreadsheet operation could actually work against.
    const headers = ['SR.', 'PO Date', 'PO Number', 'Party Name', 'GRN No.', 'GRN Date', 'Invoice Qty', 'GRN Qty', 'Diff', 'Status', 'Ticket No.', 'Ticket Open Date', 'Ticket Close Date', 'Remark', 'Credit/Debit Note', 'Note No.'];
    const dataRows = [];
    visibleGroups.forEach(g => {
      g.grns.forEach(r => {
        dataRows.push([
          dataRows.length + 1, toExcelDate(g.poDate), g.poNo, g.partyNames.join(', '), r.grn_no, toExcelDate(r.grn_date),
          g.invoiceQty != null ? Number(g.invoiceQty) : '', r.grn_qty != null ? Number(r.grn_qty) : '', g.diff != null ? Number(g.diff) : '', displayStatus(r.status),
          r.ticket_no || '', toExcelDate(r.ticket_open_date), toExcelDate(r.ticket_close_date), r.remark || '',
          r.cn_dn_type || '', r.cn_dn_no || '',
        ]);
      });
    });
    const ws = XLSX.utils.aoa_to_sheet([headers, ...dataRows]);
    // aoa_to_sheet stores a JS Date input as a type 'n' cell carrying a
    // default numFmt (m/d/yy), not a type 'd' cell — override that numFmt so
    // every date column displays (and sorts) the same, explicit way.
    const DATE_COLS = [1, 5, 11, 12];
    for (let r = 1; r <= dataRows.length; r++) {
      DATE_COLS.forEach(c => {
        const cell = ws[XLSX.utils.encode_cell({ r, c })];
        if (cell && cell.t === 'n') cell.z = 'dd-mmm-yyyy';
      });
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Goods Received Note');
    XLSX.writeFile(wb, `Goods_Received_Note_${todayISO()}.xlsx`);
  }

  // Bundles the POD for every PO currently visible under the applied
  // filters (status/search/date/month/year — same `visibleGroups` the table
  // and Excel export already use) into one zip, named <invoice_no>_<waybill_no>.
  async function downloadPodZip() {
    if (visibleGroups.length === 0) return;
    setPodZipping(true);
    try {
      const res = await fetch(`${API_BASE}/grn-pod-download-zip`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ po_numbers: visibleGroups.map(g => g.poNo) }),
      });
      if (!res.ok) throw new Error(await res.text().catch(() => 'Failed to download PODs'));
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `GRN_PODs_${todayISO()}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      notifyEditBlocked(err.message || 'Failed to download PODs');
    }
    setPodZipping(false);
  }

  /* ---- TanStack table: headless sorting over every column ---- */
  const columnHelper = useMemo(() => createColumnHelper(), []);
  const columns = useMemo(() => [
    columnHelper.display({
      id: 'sr', header: '#',
      // Reads pagination off the table instance (not component closure) so
      // the numbering stays correct without needing `pagination` in this
      // useMemo's deps — page 2 starts at 51, not back at 1.
      cell: i => {
        const { pageIndex, pageSize } = i.table.getState().pagination;
        return <span style={S.numTab}>{pageIndex * pageSize + i.row.index + 1}</span>;
      },
    }),
    columnHelper.accessor('poDate', {
      header: 'PO Date', cell: i => formatDate(i.getValue()), sortingFn: 'datetime',
    }),
    columnHelper.accessor('poNo', {
      header: 'PO Number',
      cell: i => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--grn-purple)', fontWeight: 600 }}>
          {i.getValue() || '—'}
          {i.getValue() && (
            <span onClick={(e) => { e.stopPropagation(); copyToClipboard(i.getValue(), () => notifyEditBlocked('PO Number copied!')); }} style={S.copyIcon}>
              <Copy size={ICON.xs - 1} />
            </span>
          )}
        </span>
      ),
    }),
    columnHelper.accessor(g => g.partyNames.join(', '), { id: 'party', header: 'Party' }),
    columnHelper.accessor(g => g.grns.length, { id: 'grnCount', header: 'GRNs', cell: i => <span style={S.numTab}>{i.getValue()}</span> }),
    columnHelper.accessor('diff', {
      header: 'Diff',
      cell: i => {
        const v = i.getValue();
        const color = v === null || v === 0 ? 'var(--grn-text-faint)' : 'var(--grn-error)';
        return <span className={v ? 'grn-diff-pulse' : ''} style={{ ...S.numTab, fontWeight: 700, color, display: 'inline-block' }}>{v ?? '—'}</span>;
      },
    }),
    columnHelper.accessor(g => worstStatusOf(g), {
      id: 'status', header: 'Status',
      cell: i => {
        const group = i.row.original;
        const worst = i.getValue();
        const st = statusStyle(worst);
        const mixed = isMixedStatus(group);
        return (
          <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ ...S.statusBadge, background: st.bg, color: st.color, borderColor: st.border, width: 'fit-content' }}>{displayStatus(worst)}</span>
            {mixed && <span style={{ fontSize: 10.5, color: 'var(--grn-text-faint)' }}>mixed across {group.grns.length} GRNs</span>}
          </span>
        );
      },
    }),
    columnHelper.display({
      id: 'cnDn', header: 'CN/DN No.',
      cell: i => <CnDnCell group={i.row.original} user={user} onSave={updateRow} />,
    }),
    columnHelper.display({
      id: 'ticket', header: 'Ticket',
      cell: i => hasAnyTicket(i.row.original) ? <TicketIcon size={ICON.lg} style={{ color: 'var(--grn-purple)' }} /> : <span style={{ color: 'var(--grn-border-soft)' }}>—</span>,
    }),
  ], [columnHelper, user, updateRow]);

  const table = useReactTable({
    data: visibleGroups,
    columns,
    state: { sorting, pagination },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Whenever the filtered set changes (status/search/date), the current page
  // may no longer exist (e.g. page 3 of a now-shorter list) — snap back to
  // page 1 rather than showing an empty page.
  useEffect(() => { setPagination(p => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 })); }, [visibleGroups.length]);

  return (
    <div className="grn-page" data-tutorial="grn-filters" data-grn-theme={dark ? 'dark' : undefined} style={S.page}>
      <style>{EMBEDDED_CSS}</style>
      {dark && <GrnBlurryBlobBackground />}

      {/* Unified sticky header bar — page name, actions, and search all in one row.
          Beam background sits at z-index:0; title/actions get z-index:1 so they
          stay fully legible over it. */}
      <div style={S.headerBar}>
        <HeaderBeamBackground />
        <h1 style={{ ...S.h1, position: 'relative', zIndex: 1 }}>Goods Received <span style={{ fontWeight: 600, color: '#C9B7EA' }}>Note</span></h1>
        <div style={{ ...S.headerActions, position: 'relative', zIndex: 1 }}>
          {/* Upload/Download — slide left in sync with the search's own
              width transition so the expanding search never covers them. */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            transition: 'transform 300ms cubic-bezier(0.34, 1.56, 0.64, 1)',
            transform: searchExpanded ? `translateX(-${GRN_SEARCH_EXPAND_DELTA}px)` : 'none',
          }}>
            <button style={S.btnGhost} onClick={() => { if (!hasEditAccess(user, 'po_so_lifecycle', 'grn_management')) return notifyEditBlocked(); setUploadOpen(true); }}><Upload size={ICON.sm} />Upload File</button>
            <button style={S.btnGreen} onClick={exportExcel}><Download size={ICON.sm} />Download Excel</button>
            <button style={S.btnGhost} onClick={downloadPodZip} disabled={podZipping || visibleGroups.length === 0}>
              <FileText size={ICON.sm} />{podZipping ? 'Zipping…' : 'Download Bulk POD'}
            </button>
          </div>
          <GrowingSearch value={search} onChange={setSearch} onExpandChange={setSearchExpanded} />
        </div>
      </div>

      {/* Persistent 2/3 (filters + table) / 1/3 (KPI <-> detail) split.
          Below ~1024px, .grn-body/.grn-right-col CSS (media query, !important
          since inline styles otherwise always win) drop this to a single
          stacked column instead of squeezing the split at any width. */}
      <div className="grn-body" style={S.body}>
        <div style={S.leftCol}>
          <div style={S.filterBar}>
            <FluidStatusTabs value={statusFilter} onChange={(v) => { setStatusFilter(v); setSelectedKey(null); setUploadOpen(false); }} />
            <div style={S.filterDivider} />
            <DateRangePicker from={dateFrom} to={dateTo} onChange={(f, t) => { setDateFrom(f); setDateTo(t); }} onClear={clearDateRange} />
            <MonthYearMenu label="Month" value={selectedMonth} options={MONTH_OPTIONS}
              onChange={(m) => { setSelectedMonth(m); applyMonthYear(m ? Number(m) : null, selectedYear ? Number(selectedYear) : null); }} />
            <MonthYearMenu label="Year" value={selectedYear} options={YEAR_OPTIONS}
              onChange={(y) => { setSelectedYear(y); applyMonthYear(selectedMonth ? Number(selectedMonth) : null, y ? Number(y) : null); }} />
            {(dateFrom || dateTo || selectedMonth || selectedYear) && (
              <button type="button" className="grn-tab" style={S.clearFiltersBtn} onClick={clearDateRange}>
                <X size={ICON.xs} />Clear
              </button>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', margin: '10px 0' }}>
            <span style={{ fontSize: 12.5, color: 'var(--grn-text)' }}>
              {loading ? 'Loading…' : `Showing ${visibleGroups.length} of ${groupsByPo.size} POs (${dateFiltered.length} of ${rows.length} GRNs)`}
            </span>
            {!loading && <Pagination table={table} />}
          </div>

          {loadError && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 16px', borderRadius: 10, background: 'var(--grn-pink-tint)', color: 'var(--grn-error)', fontSize: 13, marginBottom: 14 }}>
              <AlertCircle size={ICON.md} />Couldn't load GRNs: {loadError}
            </div>
          )}

          <div style={S.tableWrap}>
            <div className="grn-table-header" style={S.tableHeader}>
              {table.getHeaderGroups().map(hg => hg.headers.map(header => (
                <div key={header.id} style={{ ...S.th, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }} onClick={header.column.getToggleSortingHandler()}>
                  {flexRender(header.column.columnDef.header, header.getContext())}
                  {{ asc: <ArrowUp size={11} />, desc: <ArrowDown size={11} /> }[header.column.getIsSorted()] ?? (header.column.getCanSort() ? <ArrowUpDown size={11} style={{ opacity: 0.4 }} /> : null)}
                </div>
              )))}
            </div>

            {loading && (
              <div style={S.skeletonWrap}>
                {[...Array(6)].map((_, i) => <div key={i} className="grn-skeleton-row" style={{ ...S.skeletonRow, animationDelay: `${i * 80}ms` }} />)}
              </div>
            )}

            {!loading && table.getRowModel().rows.map(row => {
              const g = row.original;
              return (
                <div key={row.id} className="grn-table-row" style={{ ...S.row, background: g.key === selectedKey ? 'var(--grn-purple-tint)' : undefined }} onClick={() => setSelectedKey(g.key)}>
                  {row.getVisibleCells().map(cell => (
                    <div key={cell.id} style={S.td}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</div>
                  ))}
                </div>
              );
            })}
            {!loading && visibleGroups.length === 0 && (
              <div style={S.emptyState}>
                <AlertCircle size={ICON.lg} style={{ color: 'var(--grn-border-soft)' }} />
                <div style={{ fontWeight: 600, color: 'var(--grn-text-muted)' }}>No GRNs match this view</div>
                <div style={{ fontSize: 12.5, color: 'var(--grn-text-faint)' }}>Try a different status, date range, or search term.</div>
              </div>
            )}
          </div>

          {visibleGroups.length > 0 && (
            <div style={S.pager}>
              <span style={{ fontSize: 12.5, color: 'var(--grn-text-muted)' }}>
                Rows {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}
                –{Math.min((table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize, visibleGroups.length)} of {visibleGroups.length}
              </span>
              <Pagination table={table} />
            </div>
          )}
        </div>

        {/* Right column — always present. KPI view by default; swaps to
            Upload or a selected PO's detail in place. Never an overlay. */}
        <div className="grn-right-col" style={S.rightCol}>
          <AnimatePresence mode="wait">
            {uploadOpen ? (
              <UploadView
                key="upload"
                vendorFiles={vendorFiles}
                uploading={uploading}
                uploadSummary={uploadSummary}
                onFile={handleVendorFile}
                onUploadAll={handleUploadAll}
                onBack={closeUploadModal}
              />
            ) : selectedGroup ? (
              <DetailView
                key="detail"
                group={selectedGroup}
                hasImage={selectedGroup.poNo ? poNumbersWithImages.has(String(selectedGroup.poNo).trim().toUpperCase()) : false}
                user={user}
                onSave={updateRow}
                onBack={() => setSelectedKey(null)}
              />
            ) : (
              <KpiView key="kpi" dateFiltered={dateFiltered} statusCounts={statusCounts} />
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/* ---------------------------------------------------------------------- */
/* Embedded CSS — pulse animation, calendar theming, fluid-tab pill,        */
/* skeleton loading rows, reduced-motion fallback for every JS-driven       */
/* animation on this page.                                                  */
/* ---------------------------------------------------------------------- */
const EMBEDDED_CSS = `
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@300;400;500;600;700&display=swap');

/* Light/Dark theme tokens for this page only. --grn-text is primary text ink
   (light-on-dark in dark mode); --grn-accent-surface is the deliberately-dark
   chip/banner surface (Total GRNs banner, table header, active tab pill) that
   must stay a distinct dark chip in BOTH themes rather than flip to light —
   these two roles share the same #231C30 hex today but are not the same
   concept, so they get separate tokens. The header bar (#0B0A12) is already
   permanently dark by earlier request and is untouched by theming.

   Scoped to :root (not just .grn-page): Base UI's Menu.Portal renders popups
   (the Month/Year dropdowns, including the calendar's own caption dropdowns)
   directly onto document.body, OUTSIDE this component's DOM subtree — a var()
   only resolves through actual DOM ancestry, not React-tree nesting, so a
   .grn-page-scoped variable is invisible to that portaled content and every
   color silently falls back to CSS's initial value (transparent), which is
   exactly why those popups rendered with no background at all. Defining the
   same custom properties on :root instead means every node in the document —
   portaled or not — resolves them the same way, since :root is an ancestor of
   both. The (harmless) --grn-* naming means nothing outside this page ever
   references them, so this doesn't visually affect any other route. */
:root {
  --grn-surface: #fff;            --grn-surface-subtle: #FBFAFD;
  --grn-border: #ECE7F2;          --grn-border-soft: #E4DEEE;
  --grn-text: #231C30;            --grn-text-muted: #8C82A0;      --grn-text-faint: #9A90AE;
  --grn-accent-surface: #231C30;
  --grn-purple: #6E58A6;          --grn-purple-tint: #EEE9F8;
  --grn-amber-tint: #F5EDDC; --grn-amber: #9A7B3E; --grn-amber-border: #EADFC5;
  --grn-green-tint: #E7F0E8; --grn-green: #5C7E60; --grn-green-border: #D6E6D8;
  --grn-blue-tint:  #DCEAF5; --grn-blue:  #3A6B8A;
  --grn-pink-tint:  #F7E5EA; --grn-error: #C25E77;
  --grn-page-bg: transparent;
  --grn-shadow-sm: 0 1px 2px rgba(35,28,48,0.05);
  --grn-shadow-md: 0 6px 20px rgba(35,28,48,0.10);
  --grn-shadow-lg: 0 20px 50px rgba(35,28,48,0.22);
}
:root[data-grn-theme="dark"] {
  --grn-surface: #1A1823;          --grn-surface-subtle: #1F1D2A;
  --grn-border: #2D2A37;           --grn-border-soft: #34303F;
  --grn-text: #F0EFF5;             --grn-text-muted: #948CA6;      --grn-text-faint: #7C7589;
  --grn-accent-surface: #2D2A44;
  --grn-purple: #A48FD1;           --grn-purple-tint: #2D2A44;
  --grn-amber-tint: #3A331F; --grn-amber: #D8B570; --grn-amber-border: #4A4128;
  --grn-green-tint: #1F2E22; --grn-green: #8FBE97; --grn-green-border: #2C4331;
  --grn-blue-tint:  #1C2C38; --grn-blue:  #7FB3D9;
  --grn-pink-tint:  #3A2129; --grn-error: #E08BA0;
  --grn-page-bg: #141220;
  --grn-shadow-sm: 0 1px 2px rgba(0,0,0,0.30);
  --grn-shadow-md: 0 6px 20px rgba(0,0,0,0.40);
  --grn-shadow-lg: 0 20px 50px rgba(0,0,0,0.55);
}

/* Thin, translucent scrollbars everywhere on this page — table body, right
   column, and the Month/Year/calendar popups (which render into a portal
   outside this component's own DOM subtree, hence unscoped selectors here
   rather than under a wrapper class) — with no up/down arrow buttons.
   Firefox via scrollbar-width/-color, Chrome/Edge/Safari via ::-webkit-.
   This whole <style> tag unmounts with the page, so it can't leak into the
   rest of the portal once this route is left. */
* { scrollbar-width: thin; scrollbar-color: rgba(110,88,166,0.35) transparent; }
*::-webkit-scrollbar { width: 6px; height: 6px; }
*::-webkit-scrollbar-track { background: transparent; }
*::-webkit-scrollbar-thumb { background: rgba(110,88,166,0.35); border-radius: 999px; }
*::-webkit-scrollbar-thumb:hover { background: rgba(110,88,166,0.55); }
*::-webkit-scrollbar-button { display: none; width: 0; height: 0; }

@keyframes grn-diff-pulse {
  0%, 100% { transform: scale(1); text-shadow: 0 0 0 rgba(194,94,119,0); }
  50%      { transform: scale(1.06); text-shadow: 0 0 8px rgba(194,94,119,0.55); }
}
.grn-diff-pulse { animation: grn-diff-pulse 2.5s ease-in-out infinite; display: inline-block; }

.grn-table-header > div:not(:last-child), .grn-table-row > div:not(:last-child) { border-right: 1px solid var(--grn-border); }
.grn-table-row:nth-child(even) { background: var(--grn-surface-subtle); }
.grn-table-row:hover { background: var(--grn-purple-tint); cursor: pointer; }

.grn-tab { font-family: 'Space Grotesk'; transition: transform 0.15s ease, background 0.2s ease, color 0.2s ease, border-color 0.2s ease; cursor: pointer; }
.grn-tab:active { transform: scale(0.96); }

.grn-grow-search { transition: width 300ms cubic-bezier(0.34, 1.56, 0.64, 1), border-color 200ms ease, box-shadow 200ms ease; }
.grn-grow-search:focus-within { border-color: #6E58A6 !important; box-shadow: 0 0 0 4px rgba(110,88,166,0.35) !important; }
/* App.css has a global 'input:focus { background: #fff !important }' rule
   (a shared focus style used app-wide) that was winning over this input's
   own inline background the moment it was focused — the "white box"
   reappearing only while typing. Beating it with a more specific selector
   that also carries !important, rather than touching the shared file. */
.grn-grow-search input:focus { background: ${GROW_SEARCH_BG} !important; border: none !important; box-shadow: none !important; outline: none !important; }
.grn-grow-search.is-pulsing { animation: grn-search-glow 260ms ease-out; }
@keyframes grn-search-glow {
  0%   { box-shadow: 0 0 0 0 rgba(110,88,166,0.5); }
  100% { box-shadow: 0 0 0 7px rgba(110,88,166,0); }
}
@media (prefers-reduced-motion: reduce) {
  .grn-grow-search { transition: none; }
  .grn-grow-search.is-pulsing { animation: none; }
}

.grn-calendar .rdp-root {
  --rdp-accent-color: var(--grn-purple);
  --rdp-accent-background-color: var(--grn-purple-tint);
  --rdp-today-color: var(--grn-purple);
  /* Halved from react-day-picker's defaults (44/42/36/44px) per feedback
     that the calendar was rendering too large. */
  --rdp-day-height: 22px;
  --rdp-day-width: 22px;
  --rdp-day_button-height: 21px;
  --rdp-day_button-width: 21px;
  --rdp-nav_button-height: 1.125rem;
  --rdp-nav_button-width: 1.125rem;
  --rdp-nav-height: 1.375rem;
  font-family: 'Space Grotesk';
  font-size: 12px;
  color: var(--grn-text);
}
.grn-calendar .rdp-day_button:hover { background: var(--grn-purple-tint); }
.grn-calendar .rdp-weekday { color: var(--grn-text-muted); }
.grn-calendar .rdp-day_button { color: var(--grn-text); }
.grn-calendar .rdp-outside .rdp-day_button,
.grn-calendar .rdp-day_button:disabled { color: var(--grn-text-faint); }
.grn-calendar .rdp-selected .rdp-day_button { color: #fff; }
/* The day/weekday <td> cells are auto-sized by the browser's table layout
   algorithm, which can end up wider than the --rdp-day-width hint (measured
   ~49px actual vs a 22px hint) — table-layout:fixed plus explicit widths on
   every cell forces the real halved size instead of just the button inside it. */
.grn-calendar .rdp-month_grid { table-layout: fixed; }
.grn-calendar .rdp-day, .grn-calendar .rdp-weekday { width: 26px; max-width: 26px; padding: 0; }
/* react-day-picker's default stacks multiple months vertically; shadcn's
   Calendar puts them side by side via a Tailwind md:flex-row breakpoint —
   doing the same directly here since this page has no Tailwind. */
.grn-calendar .rdp-months { display: flex; flex-direction: row; flex-wrap: nowrap; gap: 20px; }

.grn-spinner { animation: grn-spin 0.7s linear infinite; }
@keyframes grn-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .grn-spinner { animation: none; } }

.grn-beam-bg { position: absolute; inset: 0; display: flex; overflow: hidden; border-radius: 16px; z-index: 0; pointer-events: none; background: #0B0A12; }
.grn-beam-lane { position: relative; flex: 1 0 auto; width: 34px; height: 100%; }
.grn-beam-lane::before { content: ''; position: absolute; inset: 0; left: 50%; width: 1px; background: rgba(230,222,250,0.06); transform: rotate(10deg); }
.grn-beam {
  position: absolute; left: 50%; top: -20%; width: 2px; height: var(--length);
  transform: translateX(-50%) rotate(10deg); opacity: 0;
  background: linear-gradient(180deg, transparent, #E4DBF5, transparent);
  animation: grn-meteor var(--duration) var(--delay) ease-in-out infinite backwards;
}
@keyframes grn-meteor {
  0%   { top: -20%; opacity: 0; }
  12%  { opacity: var(--opacity); }
  88%  { opacity: var(--opacity); }
  100% { top: 120%; opacity: 0; }
}
@media (prefers-reduced-motion: reduce) { .grn-beam { animation: none; opacity: 0; } }

.grn-blob-bg { position: fixed; inset: 0; z-index: 0; overflow: hidden; pointer-events: none; }
.grn-blob {
  position: absolute; width: 480px; height: 480px; border-radius: 50%;
  filter: blur(90px); opacity: 0.55; mix-blend-mode: screen;
  animation: grn-pop-blob 5s ease-in-out infinite;
}
.grn-blob:nth-child(1) { top: -10%; left: -8%; }
.grn-blob:nth-child(2) { top: -14%; right: -10%; }
.grn-blob:nth-child(3) { bottom: -16%; left: 30%; }
@keyframes grn-pop-blob {
  0%   { transform: scale(1); }
  33%  { transform: scale(1.2); }
  66%  { transform: scale(0.8); }
  100% { transform: scale(1); }
}
@media (prefers-reduced-motion: reduce) { .grn-blob { animation: none; } }

@keyframes grn-skeleton-shimmer {
  0%   { opacity: 0.55; }
  50%  { opacity: 1; }
  100% { opacity: 0.55; }
}
.grn-skeleton-row { animation: grn-skeleton-shimmer 1.3s ease-in-out infinite; }

@media (prefers-reduced-motion: reduce) {
  .grn-diff-pulse { animation: none; }
  .grn-tab { transition: none; }
  .grn-skeleton-row { animation: none; }
}

/* Responsive: below this, the 2/3+1/3 split just squeezed the right column
   unreadably narrow instead of adapting — stack it under the table instead.
   !important is required here because inline styles otherwise always beat a
   plain media-query rule regardless of specificity. */
@media (max-width: 1024px) {
  .grn-body { flex-direction: column !important; }
  .grn-right-col {
    position: static !important; top: auto !important; max-height: none !important;
    width: 100% !important; max-width: none !important; min-width: 0 !important;
  }
}
`;

/* ---------------------------------------------------------------------- */
/* Styles                                                                  */
/* ---------------------------------------------------------------------- */
const S = {
  page: { color: 'var(--grn-text)', fontFamily: "'Space Grotesk', sans-serif", background: 'var(--grn-page-bg)', position: 'relative' },
  numTab: { fontVariantNumeric: 'tabular-nums' },

  headerBar: {
    position: 'sticky', top: 0, zIndex: 20, display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap',
    background: '#0B0A12', borderRadius: 16, border: '1px solid rgba(255,255,255,0.08)', boxShadow: SHADOW_MD,
    padding: '14px 20px', marginBottom: 18, overflow: 'hidden',
  },
  h1: { fontFamily: "'Space Grotesk'", fontWeight: 300, fontSize: 22, margin: 0, color: '#F3EFFA', letterSpacing: '-0.01em', whiteSpace: 'nowrap' },
  // paddingRight reserves room for the absolutely-positioned search pill
  // (collapsed width + a little breathing room) so the Upload/Download
  // button group — normal flow, unaware of that sibling — doesn't render
  // underneath it.
  headerActions: { display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, flexWrap: 'wrap', flex: 1, minWidth: 0, minHeight: 38, paddingRight: GRN_SEARCH_COLLAPSED_WIDTH + 16 },
  growSearch: { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 999, background: GROW_SEARCH_BG, border: '1px solid var(--grn-border)', overflow: 'hidden', flex: 'none', cursor: 'text' },
  // Anchored to the header-actions row's right edge (position:relative on
  // that row) instead of just being last-in-flow — pinning `right` means
  // growing `width` extends the box LEFTWARD (left = right - width), which
  // is what "should open towards left" needs; a normal flex-flow element
  // can only grow toward whichever side has room, i.e. rightward/off-edge.
  growSearchAnchored: { position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)' },

  btnGhost: { display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderRadius: 10, border: '1px solid var(--grn-border-soft)', background: 'var(--grn-surface)', color: 'var(--grn-text)', fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: "'Space Grotesk'", whiteSpace: 'nowrap' },
  btnGreen: { display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderRadius: 10, border: 'none', background: '#5C7E60', color: '#fff', fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: "'Space Grotesk'", whiteSpace: 'nowrap' },
  btnGhostSmall: { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 12px', borderRadius: 8, border: '1px solid var(--grn-border-soft)', background: 'var(--grn-surface)', color: 'var(--grn-purple)', fontWeight: 500, fontSize: 12, cursor: 'pointer', fontFamily: "'Space Grotesk'" },

  body: { display: 'flex', gap: 20, alignItems: 'flex-start', position: 'relative', zIndex: 1 },
  leftCol: { flex: '1 1 66%', minWidth: 0 },
  rightCol: {
    flex: '0 0 33%', minWidth: 280, maxWidth: 380,
    position: 'sticky', top: STICKY_TOP, alignSelf: 'flex-start',
    maxHeight: `calc(100vh - ${STICKY_TOP + 24}px)`, overflowY: 'auto',
    background: 'var(--grn-surface)', borderRadius: 16, border: '1px solid var(--grn-border)', boxShadow: SHADOW_SM,
  },

  filterBar: {
    display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
    background: 'var(--grn-surface)', border: '1px solid var(--grn-border)', borderRadius: 14, boxShadow: SHADOW_SM,
    padding: '10px 14px', marginBottom: 14,
  },
  filterDivider: { width: 1, alignSelf: 'stretch', background: 'var(--grn-border)', margin: '0 2px' },

  fluidNav: { display: 'inline-flex' },
  fluidList: { position: 'relative', display: 'flex', gap: 2, background: 'var(--grn-surface-subtle)', borderRadius: 999, padding: 4 },
  fluidTab: { position: 'relative', border: 'none', background: 'transparent', padding: '8px 14px', borderRadius: 999, fontSize: 12.5, fontWeight: 600 },
  fluidPill: { position: 'absolute', inset: 0, borderRadius: 999, background: 'var(--grn-accent-surface)', zIndex: 0 },
  fluidTabContent: { position: 'relative', zIndex: 1, display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' },

  dateBtn: { display: 'flex', alignItems: 'center', gap: 7, padding: '8px 13px', borderRadius: 9, border: '1px solid var(--grn-border)', background: 'var(--grn-surface)', color: 'var(--grn-text)', fontSize: 12.5 },
  clearFiltersBtn: { display: 'flex', alignItems: 'center', gap: 5, padding: '8px 12px', borderRadius: 9, border: '1px solid var(--grn-border-soft)', background: 'var(--grn-purple-tint)', color: 'var(--grn-purple)', fontSize: 12.5, fontWeight: 600 },
  calendarCaptionTrigger: { display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent', color: 'var(--grn-text)', fontWeight: 700, fontSize: 13, cursor: 'pointer', fontFamily: "'Space Grotesk'", padding: '2px 4px' },
  // Well above everything else in the app — this popup kept appearing
  // behind other elements at z-index 150 despite that seeming high enough
  // in isolation, because its actual stacking-context comparison happens at
  // whatever ancestor level establishes one, not necessarily against this
  // page's own elements. Matching the same headroom given to the Base UI
  // Menu popups below rather than re-deriving the exact ancestor chain.
  calendarPopover: { position: 'absolute', top: '110%', left: 0, zIndex: 500, background: 'var(--grn-surface)', borderRadius: 14, boxShadow: SHADOW_LG, border: '1px solid var(--grn-border)', padding: 10 },
  clearCalBtn: { width: '100%', marginTop: 6, padding: '8px', borderRadius: 8, border: '1px solid var(--grn-border-soft)', background: 'var(--grn-purple-tint)', color: 'var(--grn-text-muted)', fontSize: 12.5, cursor: 'pointer', fontFamily: "'Space Grotesk'" },

  menuPopup: { background: 'var(--grn-surface)', borderRadius: 12, boxShadow: SHADOW_LG, border: '1px solid var(--grn-border)', padding: 6, minWidth: 140, maxHeight: 280, overflowY: 'auto', zIndex: 500 },
  menuItem: { padding: '9px 12px', borderRadius: 8, fontSize: 13, color: 'var(--grn-text)', cursor: 'pointer', fontFamily: "'Space Grotesk'", outline: 'none' },

  // `overflowX: 'auto'` alone forces the browser to compute overflow-y as a
  // scroll container too (CSS spec: mixing 'visible' with a scrolling value
  // on the other axis promotes 'visible' to 'auto') — so this box was always
  // going to be its OWN sticky containing block, never the page. Leaning
  // into that instead of fighting it: a fixed max-height + explicit
  // overflowY make it a genuine self-scrolling table with its own sticky
  // header (top: 0, not page-relative) — same pattern as the right column.
  tableWrap: {
    borderRadius: 16, background: 'var(--grn-surface)', border: '1px solid var(--grn-border)', boxShadow: SHADOW_SM,
    overflowX: 'auto', overflowY: 'auto', maxHeight: `calc(100vh - ${STICKY_TOP + 24}px)`,
  },
  pager: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 4px' },
  paginationNav: { display: 'flex', alignItems: 'center', gap: 4 },
  paginationArrow: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, border: '1px solid var(--grn-border-soft)', background: 'var(--grn-surface)', color: 'var(--grn-text)', cursor: 'pointer' },
  paginationLink: { minWidth: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: '1px solid transparent', background: 'transparent', color: 'var(--grn-text-muted)', fontSize: 12.5, fontWeight: 500, cursor: 'pointer', fontFamily: "'Space Grotesk'" },
  paginationLinkActive: { background: '#6E58A6', color: '#fff', fontWeight: 700 },
  paginationEllipsis: { minWidth: 24, textAlign: 'center', color: 'var(--grn-text-faint)', fontSize: 12.5 },
  tableHeader: {
    display: 'grid', gridTemplateColumns: '0.35fr 0.9fr 1.2fr 1.2fr 0.6fr 0.7fr 0.9fr 0.8fr 0.6fr',
    background: 'var(--grn-accent-surface)', color: '#fff', minWidth: 780, position: 'sticky', top: 0, zIndex: 5,
    borderRadius: '16px 16px 0 0',
  },
  th: { padding: '13px 16px', fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textAlign: 'left' },
  row: { display: 'grid', gridTemplateColumns: '0.35fr 0.9fr 1.2fr 1.2fr 0.6fr 0.7fr 0.9fr 0.8fr 0.6fr', borderBottom: '1px solid var(--grn-border)', alignItems: 'center', minWidth: 780 },
  td: { padding: '13px 16px', fontSize: 13, textAlign: 'left', display: 'flex', alignItems: 'center' },
  emptyState: { padding: '48px 18px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 },
  skeletonWrap: { padding: 14, display: 'flex', flexDirection: 'column', gap: 10 },
  skeletonRow: { height: 44, borderRadius: 10, background: 'linear-gradient(90deg, var(--grn-border), var(--grn-surface-subtle), var(--grn-border))' },

  statusBadge: { padding: '5px 12px', borderRadius: 9, fontSize: 12, fontWeight: 600, border: '1px solid' },
  copyIcon: { flex: 'none', display: 'inline-flex', alignItems: 'center', color: 'var(--grn-text-faint)', cursor: 'pointer' },
  fieldLabel: { fontSize: 11.5, fontWeight: 600, color: 'var(--grn-text-muted)', marginBottom: 6, letterSpacing: '0.02em' },
  select: { padding: '8px 30px 8px 14px', borderRadius: 9, fontSize: 12.5, fontWeight: 600, border: '1px solid var(--grn-border)', cursor: 'pointer', fontFamily: "'Space Grotesk'" },
  ticketInput: { width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid var(--grn-border)', background: 'var(--grn-surface-subtle)', fontSize: 12.5, fontWeight: 500, color: 'var(--grn-text)', fontFamily: "'Space Grotesk'" },
  ticketChip: { padding: '3px 9px', borderRadius: 20, background: 'var(--grn-purple-tint)', color: 'var(--grn-purple)', fontSize: 11.5, fontWeight: 600 },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(35,28,48,0.42)', backdropFilter: 'blur(2px)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 },
  modal: { width: 560, maxHeight: '82vh', overflowY: 'auto', background: 'var(--grn-surface)', borderRadius: 20, boxShadow: SHADOW_LG },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '24px 28px', borderBottom: '1px solid var(--grn-border)', position: 'sticky', top: 0, background: 'var(--grn-surface)', borderRadius: '20px 20px 0 0' },
  closeBtn: { width: 34, height: 34, borderRadius: 10, background: 'var(--grn-purple-tint)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--grn-text-muted)', flex: 'none' },
  vendorRow: { display: 'flex', alignItems: 'center', gap: 16, padding: '16px 18px', borderRadius: 14, background: 'var(--grn-surface-subtle)', border: '1px solid var(--grn-border)' },
  vendorRowCompact: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'var(--grn-surface-subtle)', border: '1px solid var(--grn-border)' },
  chooseFileCompact: { flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', width: 30, height: 30, borderRadius: 8, border: '1px solid var(--grn-border-soft)', background: 'var(--grn-surface)', color: 'var(--grn-purple)', cursor: 'pointer' },
  vendorAvatar: { width: 44, height: 44, borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 600, fontSize: 15, flex: 'none' },
  chooseFile: { flex: 'none', display: 'flex', alignItems: 'center', gap: 7, padding: '10px 16px', borderRadius: 10, border: '1px solid var(--grn-border-soft)', background: 'var(--grn-surface)', color: 'var(--grn-purple)', fontWeight: 500, fontSize: 12.5, cursor: 'pointer', fontFamily: "'Space Grotesk'" },
  modalFooter: { padding: '16px 28px 24px', borderTop: '1px solid var(--grn-border)', display: 'flex', justifyContent: 'flex-end', gap: 12 },
  btnCancel: { padding: '11px 20px', borderRadius: 10, border: '1px solid var(--grn-border-soft)', background: 'var(--grn-surface)', color: 'var(--grn-text-muted)', fontWeight: 500, fontSize: 13.5, cursor: 'pointer', fontFamily: "'Space Grotesk'" },
  btnPrimary: { padding: '11px 24px', borderRadius: 10, border: 'none', background: '#6E58A6', color: '#fff', fontWeight: 500, fontSize: 13.5, cursor: 'pointer', fontFamily: "'Space Grotesk'" },

  bentoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, padding: 16 },
  bentoSpan2: { gridColumn: 'span 2' },
  bentoCard: { borderRadius: 16, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 6, minHeight: 76, justifyContent: 'center' },
  statLabel: { fontSize: 10.5, color: 'var(--grn-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },

  backBtn: { display: 'flex', alignItems: 'center', gap: 6, border: 'none', background: 'transparent', color: 'var(--grn-purple)', fontWeight: 600, fontSize: 12.5, cursor: 'pointer', fontFamily: "'Space Grotesk'", padding: '16px 20px 0' },
  panelHeader: { padding: '10px 20px 14px' },
  panelStats: { display: 'flex', gap: 8, padding: '0 20px 16px' },
  panelStat: { flex: 1, textAlign: 'center', background: 'var(--grn-surface-subtle)', borderRadius: 12, padding: '10px 4px' },
  panelStatLabel: { fontSize: 10, color: 'var(--grn-text-muted)', textTransform: 'uppercase', letterSpacing: '0.03em', marginBottom: 4 },
  panelStatValue: { fontSize: 18, fontWeight: 700, color: 'var(--grn-text)', fontVariantNumeric: 'tabular-nums' },
  panelSection: { padding: '16px 20px', borderBottom: '1px solid var(--grn-border)' },
  panelSectionTitle: { fontSize: 11.5, fontWeight: 600, color: 'var(--grn-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 10 },
  panelEmptyNote: { fontSize: 12.5, color: 'var(--grn-text-faint)' },
  thumb: { width: 60, height: 60, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--grn-border)' },
  thumbStacked: { width: 52, height: 52, objectFit: 'cover', borderRadius: 10, border: '2px solid var(--grn-surface-subtle)', boxShadow: SHADOW_SM, display: 'block' },

  podCard: { width: '100%', background: 'var(--grn-surface)', border: '1px solid var(--grn-border-soft)', borderRadius: 14, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 },
  podInfoRow: { flex: 1, minWidth: 180, display: 'flex', alignItems: 'center', gap: 10, background: 'var(--grn-purple-tint)', borderRadius: 10, padding: '10px 12px' },
  podOpenBtn: { textAlign: 'center', padding: '9px 20px', borderRadius: 10, border: 'none', background: 'var(--grn-purple)', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: "'Space Grotesk'", textDecoration: 'none' },
  podOpenBtnAlt: { textAlign: 'center', padding: '9px 20px', borderRadius: 10, border: 'none', background: 'var(--grn-blue)', color: '#fff', fontWeight: 700, fontSize: 12.5, cursor: 'pointer', fontFamily: "'Space Grotesk'", textDecoration: 'none' },
  imageCountBadge: { flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: 30, height: 30, padding: '0 8px', borderRadius: 999, background: '#6E58A6', color: '#fff', fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  imagePlusTile: { display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#6E58A6', color: '#fff', fontSize: 12.5, fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  panelGrnRow: { border: '1px solid var(--grn-border)', borderRadius: 12, overflow: 'hidden' },
  panelGrnHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 13px', cursor: 'pointer', background: 'var(--grn-surface-subtle)' },
  panelGrnBody: { padding: '14px', display: 'flex', flexDirection: 'column', gap: 14, borderTop: '1px solid var(--grn-border)' },
};
